import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { sendToGemini } from '../utils/api';
import apiService from '../services/apiService';
import logger from '../utils/logger';
import { startStickToBottom, hasScrolledUpFrom } from '../utils/stickToBottom';
import { startRevealGate } from '../utils/revealAfterPositioning';
import {
  findRecoverableReply,
  hasPendingGap,
  markAbandoned,
  markRetryLater,
  clearRetryLater,
  markDeliveryFailed,
  isDeliveryFailed,
  clearDeliveryFailed,
} from '../utils/pendingReply';
import { hasInteractiveMcpApp, mergeMcpApps } from '../utils/mcpApps';

// 把 MCP Apps 互動畫面（AGUI-1274）掛到 bot 訊息上：依 toolCallId 去重、剔除無效標記；
// 沒有任何畫面時不加 mcpApps 欄位，訊息形狀與既有完全相同
const withMcpApps = (message, ...lists) => {
  const mcpApps = mergeMcpApps(...lists);
  return mcpApps.length > 0 ? { ...message, mcpApps } : message;
};

// 發送流程會呼叫 updateChatMessages 兩次；guest 必須帶固定 id，否則第二次 normalize 會產生新 UUID 造成 DB 重複列
const generateMessageId = () => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch (e) {
    // ignore
  }
  return `msg-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

// options 可傳入 WebSocket 客戶端與狀態，當連線時改以 WS 發送
export const useChat = (
  chatHistory,
  updateChatMessages,
  getCurrentMessages,
  options = {}
) => {
  const { stompClient, wsConnected, wsSendDestination, isHumanMode, enableAi, disableAi, isNewChat, createNewChat, consumePreallocatedChatId, isMobile, isAuthed, authTransitioning } = options;
  const [input, setInput] = useState('');
  // ── per-chat 送出/等待狀態（取代單一全域 isLoading）──────────────────────────────
  // 一個對話從「開始送出」到「回覆落地」期間，其 chatId 會在此集合中。對外暴露的 isLoading
  // （見檔案結尾 return）是「目前檢視的對話是否在此集合」的衍生布林，因此 App／輸入框無需分辨對話，
  // 而不同對話的等待狀態彼此獨立：A 在回覆時切到 B 不會看到 B 的等待泡泡，且 B 可獨立送出。
  const [loadingChatIds, setLoadingChatIds] = useState(() => new Set());
  // ref 鏡像：供 async 送出流程（閉包）即時判斷某對話是否已在等待，避免讀到 render 當下的舊 state
  const loadingChatIdsRef = useRef(loadingChatIds);
  useEffect(() => { loadingChatIdsRef.current = loadingChatIds; }, [loadingChatIds]);
  const addLoading = useCallback((cid) => {
    if (!cid) return;
    setLoadingChatIds((prev) => (prev.has(cid) ? prev : new Set(prev).add(cid)));
  }, []);
  const removeLoading = useCallback((cid) => {
    if (!cid) return;
    setLoadingChatIds((prev) => {
      if (!prev.has(cid)) return prev;
      const next = new Set(prev);
      next.delete(cid);
      return next;
    });
  }, []);
  // 目前檢視對話 id 的 ref 鏡像：送出回應到達時用來判斷使用者是否仍停在該對話（決定走逐字動畫或即時落地）
  const currentChatIdRef = useRef(chatHistory.currentChatId);
  useEffect(() => { currentChatIdRef.current = chatHistory.currentChatId; }, [chatHistory.currentChatId]);
  // 補收「處理中」時被鎖定的對話 id 集合（取代單一值，讓多個對話能同時各自補收、互不干擾）。
  // refresh／回前景後偵測到前一題仍在後端處理（GET /reply 回 processing）時，把該對話的 chatId
  // 加進這個集合，用途有二：
  //   1. 在該對話重建「回覆中」等待畫面（打字泡泡），避免畫面看似卡死。
  //   2. 鎖住「該對話」的輸入，避免使用者對同一個 GenAIus session 再問第二題——
  //      GenAIus 同一 session 一次只處理一題，併發送出第二題只會拿到空回覆。
  // 只綁定「這一個對話」：切換或開新對話時輸入不受影響，仍可正常提問（新對話 = 新 session，互不干擾）。
  const [recoveringChatIds, setRecoveringChatIds] = useState(() => new Set());
  const addRecovering = useCallback((cid) => {
    if (!cid) return;
    setRecoveringChatIds((prev) => (prev.has(cid) ? prev : new Set(prev).add(cid)));
  }, []);
  const removeRecovering = useCallback((cid) => {
    if (!cid) return;
    setRecoveringChatIds((prev) => {
      if (!prev.has(cid)) return prev;
      const next = new Set(prev);
      next.delete(cid);
      return next;
    });
  }, []);
  // 最終落地寫入失敗（IndexedDB 讀寫被拒：私密模式、被瀏覽器封鎖、配額不足）的對話集合。
  // updateChatMessages 若在「讀既有紀錄」那一步就 reject，chatHistory 根本沒被更新，本地陣列
  // 最後一則仍是那則未回覆的提問，hasPendingGap 會持續為 true 而鎖住輸入；而這一題已經沒有任何人
  // 會再收尾（commitFinal 對同一則只跑一次），等於永久鎖死。故對這些對話不因 gap 上鎖。
  // 刻意用 state 而非 ref：isAwaitingReply 於 render 期間計算，若 catch 裡的 setStreamFor／
  // removeLoading 剛好都是 no-op（串流已清、loading 已清）就不會 re-render，ref 的變更便反映不到畫面。
  // 不寫 pendingReply 的墓碑（markAbandoned）：那是 durable 的「使用者不要這個答案了」，會讓補收
  // 永久略過；但回覆其實還在後端暫存表，下次 IndexedDB 恢復時補收仍可能救回來，不該提前放棄。
  const [landingFailedChatIds, setLandingFailedChatIds] = useState(() => new Set());
  const addLandingFailed = useCallback((cid) => {
    if (!cid) return;
    setLandingFailedChatIds((prev) => (prev.has(cid) ? prev : new Set(prev).add(cid)));
  }, []);
  const clearLandingFailed = useCallback((cid) => {
    if (!cid) return;
    setLandingFailedChatIds((prev) => {
      if (!prev.has(cid)) return prev;
      const next = new Set(prev);
      next.delete(cid);
      return next;
    });
  }, []);
  // 本次 session 終局送達失敗的提問 id 集合（change: unify-reply-completion-signal Section 5）。
  // 與 pendingReply.markDeliveryFailed（localStorage，跨 session 持久）互補：這裡是 React state，
  // 存在的意義是保證「判定終局失敗」那一刻一定會觸發 re-render——若當下沒有任何其他等待狀態
  // 需要清除（例如 heartbeat 第一次檢查就發現提問早已超過時限，isLoading/isRecovering 從未被設過），
  // removeLoading/setStreamFor 等既有清理呼叫全部是 no-op（值本來就相同），不會觸發 re-render，
  // UI 會停留在「還在等待」的舊畫面，即使 localStorage 已經正確標記失敗也一樣看不到。
  const [deliveryFailedMsgIds, setDeliveryFailedMsgIds] = useState(() => new Set());
  const addDeliveryFailedMsg = useCallback((id) => {
    if (!id) return;
    setDeliveryFailedMsgIds((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
  }, []);
  const removeDeliveryFailedMsg = useCallback((id) => {
    if (!id) return;
    setDeliveryFailedMsgIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);
  const messagesEndRef = useRef(null);
  const lastBotMessageRef = useRef(null);
  // 最新一則使用者訊息的 DOM ref（由 App.js 掛在 .messages map 中「最後一個 sender==='guest'」的節點上）；
  // 送出後改捲到這裡置頂（比照 Gemini/Copilot），取代舊有「捲到底部」的做法（見 scrollUserMessageToTop）
  const lastUserMessageRef = useRef(null);
  // 以下送出相關狀態全部以 chatId 為 key，讓多個對話能同時各自送出、互不干擾。
  // 停止成功的對話集合：供 sendMessage.catch 判斷是否壓制該對話的錯誤訊息
  const stopSucceededByChatRef = useRef(new Set());
  // 使用者已按停止的「提問 id」集合：sendMessage 收到 sendToGemini 回傳後，若這次送出的提問
  // 在此集合中就跳過落地。
  // 刻意以 userMessageId 而非 chatId 為 key（AGUI-1082 實測）：舊版以 chatId 為 key，而旗標只在
  // 「上一題送出流程的 finally」才清除；fast-accept 模式下那個 finally 卡在 accepted-ack 補收的
  // await 裡（被停止的那題永遠等不到回覆，得撐到終局時限），於是「按停止後立刻再發問」那段窗口內
  // 送出的下一題全被誤判成「使用者已停止」而 early return：不落地、也不啟動自己的補收輪詢，
  // 後端答案生出來也沒人去拿（實測：第二題答案已 buffered 2046 字，前端全程沒發過一次 /reply）。
  // 停止本來就只該作用於「按下停止當時的那一題」。
  const stoppedUserMsgIdsRef = useRef(new Set());
  // 「停止收尾進行中」的對話集合。按停止後要等後端確認 GenAIus 真的停了（/stop 回應）才解鎖輸入框；
  // 這段期間同一對話不重複送出停止請求（避免連點）。以 chatId 為 key，收尾結束即移除。
  const stopInProgressByChatRef = useRef(new Set());
  // 同上，但用 state 讓畫面能顯示「停止中」（停止鈕停用），避免使用者以為點了沒反應而連點。
  const [stoppingChatIds, setStoppingChatIds] = useState(() => new Set());
  // 每個對話 sendToGemini 的 AbortController：停止某對話時只取消它自己的 HTTP 請求，不影響其他對話
  const sendAbortByChatRef = useRef(new Map());
  // 正常送出仍在進行中的對話集合（await sendToGemini 期間）。
  // 補收（recoverPendingReply）對「仍在送出中」的對話不得判定終局失敗——正常送出還活著就會落地回覆，
  // 或其失敗會走 _sendFailed 觸發正確的補收；避免慢速回覆（如檔案分析）被 recovery 提前判死。
  const sendInFlightByChatRef = useRef(new Set());
  // 每個對話進行中提問的 userMessageId：供 stopGeneration/終局失敗時同步寫入「已放棄」墓碑，
  // 蓋掉「按停止後、系統通知尚未落地 IndexedDB 就被 OS 銷毀 WebView」的窄競態（見 pendingReply.js）
  const inFlightUserMsgIdByChatRef = useRef(new Map());
  // 已由補收落地回覆的提問 id 集合：補收成功落地時加入，那次仍在進行中的 /send 回來後據此得知
  // 「答案已經由補收落地了」，直接放棄落地自己的內容，避免同一題重複落地（或用過期的訊息陣列覆蓋）。
  // 只在該次送出回來時消費一次即移除，不需持久化。
  const recoveryLandedUserMsgIdsRef = useRef(new Set());
  // 該對話的回覆「已經落地」（正常路徑 commitFinal 或補收 append 皆算）。
  // 仍在輪詢的補收據此停止上鎖並在下一個檢查點退出：答案既然已在畫面上，就沒有理由再鎖住輸入。
  // UI 的解鎖在落地當下即由 removeRecovering 完成，不必等輪詢醒來，故不需要可中斷的等待。
  // 每輪補收結束時消費掉、送出新一題時重置，皆為 per-chat。
  const replyLandedByChatRef = useRef(new Set());
  // 每個對話開啟後只做一次「載入補收掃描」的旗標（切換對話時重置），避免每次新增訊息都重掃
  const loadScanDoneRef = useRef(false);
  // 持有「目前這次 render」的回前景檢查邏輯，供只註冊一次的 DOM/window listener 呼叫。
  // visibilitychange／window focus listener 用 addEventListener 只掛載一次（deps=[]），若直接在
  // listener 內閉包讀取 chatHistory.currentChatId/getCurrentMessages，會永遠停在 mount 當下那次
  // render 的值（stale closure：對話切換後 listener 讀到的仍是舊 chatId）。透過此 ref 讓每次 render
  // 都更新「最新版」的檢查函式，listener 只負責呼叫 ref.current，即可讀到最新狀態。
  const checkVisibilityRecoverRef = useRef(null);
  // 觸發點2（回前景）繞過冷卻後的節流：Map<chatId, timestamp>，記錄該對話上次因回前景訊號
  // 而真正查詢的時間。與 recoveryInFlightByChatRef（防止同一對話並發查詢）是互補的兩層保護——
  // 這裡防的是「查詢已結束、使用者又極短時間內連續觸發 visibilitychange/focus」造成同一對話連打多次 /reply。
  const lastVisibilityRecoverAtByChatRef = useRef(new Map());
  const VISIBILITY_RECOVER_THROTTLE_MS = 5000;
  // 串流暫存緩衝，以 chatId 為 key：Map<chatId, { text, createdAt }>。只存在於記憶體、不經過
  // updateChatMessages；串流結束或使用者停止時移除該對話項目，正式內容仍以 updateChatMessages 落地為準。
  // text 是「後端目前已收到的真實文字」（目標值），畫面不會直接顯示它——當前檢視的對話透過下方
  // revealedText 用固定節奏追上目標值呈現逐字效果；非當前對話不做動畫、回應到達即整段落地。
  // 用 Map 而非單一物件，才能支援多個對話同時串流（不同對話 = 不同 GenAIus session，可並行）。
  const [streams, setStreams] = useState(() => new Map());
  // ref 鏡像：供只建立一次的 reveal 計時器與 async 流程讀取最新值，避免閉包過期
  const streamsRef = useRef(streams);
  useEffect(() => { streamsRef.current = streams; }, [streams]);
  // 更新某對話的串流緩衝：updater 可為新值或 (prev)=>新值；回傳 null/undefined 代表移除該對話項目
  const setStreamFor = useCallback((cid, updater) => {
    if (!cid) return;
    setStreams((prev) => {
      const cur = prev.get(cid) || null;
      const val = typeof updater === 'function' ? updater(cur) : updater;
      if (cur === val) return prev;
      const next = new Map(prev);
      if (val == null) next.delete(cid); else next.set(cid, val);
      return next;
    });
  }, []);

  // 逐字揭示動畫：revealedText 以「固定速度」追上「當前檢視對話」串流的 text 目標值，呈現打字機效果。
  // 刻意不做「落後越多追越快」的加速，因為速度忽快忽慢反而會有卡頓／衝刺感；
  // 寧可落後量大時多花一點時間追完，也要讓速度全程保持一致、感覺順暢。
  // 注意：動畫只服務「當前檢視的對話」；背景對話不逐字揭示（回應到達即整段落地）。
  const [revealedText, setRevealedText] = useState('');
  const revealedLenRef = useRef(0);
  // 每個對話「已拿到最終文字、等落地」的暫存訊息：Map<chatId, messages[]>。當前對話等逐字動畫追上後落地，
  // 非當前對話由下方 reveal 計時器即時落地。避免尾段文字未經動畫就整段出現（當前對話）。
  const pendingFinalByChatRef = useRef(new Map());
  // 收到 AI_STREAM_CLEAR 但尚未套用的對話集合：故意不在收到當下就清空畫面（那樣會在工具執行期間
  // 出現一段空白閃爍），改成先記住，等下一段 AI_STREAM_DELTA 真的送達時才真正丟棄舊內容重新開始，
  // 讓畫面在工具執行期間維持顯示前一段文字，只有新內容真正出現的瞬間才整段替換。
  //
  // 同時保留 state 與 ref 兩份（與上方 loadingChatIds 同樣的模式）：
  //   ref   — 供 DELTA handler／reveal 計時器等同步流程即時判斷，不受 render 時序影響
  //   state — 驅動畫面：這段期間工具仍在執行，需要在已顯示的過程文字下方補上「處理中」打字指示，
  //           否則畫面會停在一段看起來已經結束的文字上，使用者不知道還在跑（見 isToolProcessing）
  const [pendingClearChatIds, setPendingClearChatIds] = useState(() => new Set());
  const pendingClearByChatRef = useRef(pendingClearChatIds);
  // 兩份一起更新的輔助函式；set 一律換新參考，確保 React 偵測到變更
  const addPendingClear = useCallback((cid) => {
    if (!cid || pendingClearByChatRef.current.has(cid)) return;
    const next = new Set(pendingClearByChatRef.current);
    next.add(cid);
    pendingClearByChatRef.current = next;
    setPendingClearChatIds(next);
  }, []);
  const removePendingClear = useCallback((cid) => {
    if (!cid || !pendingClearByChatRef.current.has(cid)) return;
    const next = new Set(pendingClearByChatRef.current);
    next.delete(cid);
    pendingClearByChatRef.current = next;
    setPendingClearChatIds(next);
  }, []);
  // 正在等待「自己發送的訊息」收尾的對話集合（從 sendMessage 開始送出到真正落地為止）。
  // 後端串流結束時會幾乎同時透過 WebSocket（AI_STREAM_DONE，通常較快）跟 REST 回應（較慢）兩個管道通知；
  // 若 AI_STREAM_DONE 比 REST 回應先到又直接清空該對話串流，會把還在播放的揭示動畫/目標值一起清掉，
  // 導致畫面「突然變思考中、又整段跳出來」。此集合讓 handleAiStreamEvent 知道：這些對話的收尾時機
  // 一律交給揭示動畫/落地流程決定，DONE 事件不要提前插手。
  const awaitingOwnFinalizeByChatRef = useRef(new Set());
  // updateChatMessages 由外部傳入，可能每次 render 拿到新的函式參考；
  // 下方 setInterval 只建立一次（deps 為空陣列），需透過 ref 才能呼叫到最新版本，避免閉包過期
  const updateChatMessagesRef = useRef(updateChatMessages);
  useEffect(() => {
    updateChatMessagesRef.current = updateChatMessages;
  }, [updateChatMessages]);

  // 依 chatId 取「最新」訊息陣列的 ref 鏡像（同 streamsRef／updateChatMessagesRef 的用意）。
  // 補收是長時間執行的 async 輪詢迴圈，若直接閉包讀 chatHistory/getCurrentMessages，會永遠停在
  // 迴圈啟動那次 render 的值（stale closure），使「缺口已被正常路徑落地填補」永遠偵測不到，
  // 輪詢因此一路跑到 completed 或 DEADLINE_MS，期間持續鎖住該對話的輸入。
  const messagesForChatRef = useRef(null);
  useEffect(() => {
    messagesForChatRef.current = (cid) => {
      if (cid === chatHistory.currentChatId) return getCurrentMessages();
      const c = Array.isArray(chatHistory.chatHistory) ? chatHistory.chatHistory.find((x) => x.id === cid) : null;
      return c?.messages ?? [];
    };
  });

  // 補收流程進行中旗標，以 chatId 為 key：同一個對話的三個觸發點（發送網路錯誤、回前景、WS 重連）
  // 可能重疊，同一對話同一時間只允許一輪補收；但不同對話（例如兩個對話都 refresh 後都待補收）
  // 應各自獨立進行，不能共用一個全域旗標，否則其中一個對話的補收會把另一個對話卡成永遠 'skipped'。
  const recoveryInFlightByChatRef = useRef(new Set());
  // 正睡在退避等待中的補收輪詢：chatId → 提前喚醒它的 resolver。
  // 背景時 setTimeout 會被瀏覽器節流、甚至完全暫停（手機切走即停），於是答案早已寫進後端、
  // 前端那一輪輪詢卻還在睡；而「回到前景立刻補收」這個觸發點又會被 recoveryInFlightByChatRef
  // 擋成 'skipped'——因為那個旗標正握在這個睡著的輪詢手上。結果就是回前景後什麼都不會發生，
  // 輸入框繼續被 isRecovering 鎖住，直到被凍結的計時器自己醒來（實測可達數分鐘）。
  // 故改為讓回前景直接把它叫醒：維持「同一對話只有一輪補收」不變，不用放寬併發保護。
  const recoveryWakeByChatRef = useRef(new Map());
  // 已被使用者按停止、應盡快收工的補收輪詢（以 userMessageId 為 key）。
  // 停止之後那一題永遠不會有回覆，若讓它的輪詢照原訂退避跑到終局時限（180s）才結束，
  // 這段期間 recoveryInFlightByChatRef 一直被它握著，同一對話的下一題連自己的補收都起不來
  // （被擋成 'skipped'）。故停止時同步標記 + wakeRecovery 叫醒，讓它立刻退出並交還旗標。
  const recoveryStopUserMsgIdsRef = useRef(new Set());
  // accepted-ack 補收被 'skipped' 擋下後的補打間隔（見 sendMessage fast-accept 分支）
  const ACK_RECOVERY_RETRY_MS = 500;

  /**
   * 退避等待：時間到、或被 wakeRecovery 提前叫醒，兩者皆 resolve。
   * @param {string} chatId 對話 id（喚醒以對話為單位）
   * @param {number} ms 等待毫秒數
   * @returns {Promise<'timer'|'wake'>} 醒來的原因（僅供 log／測試判讀）
   */
  const sleepUntilWakeOrTimeout = (chatId, ms) => new Promise((resolve) => {
    const finish = (reason) => {
      // 兩條路徑共用清理：移除 resolver 並確保計時器不再觸發，避免殘留或重複喚醒
      clearTimeout(timer);
      if (recoveryWakeByChatRef.current.get(chatId) === wake) {
        recoveryWakeByChatRef.current.delete(chatId);
      }
      resolve(reason);
    };
    const wake = () => finish('wake');
    const timer = setTimeout(() => finish('timer'), ms);
    recoveryWakeByChatRef.current.set(chatId, wake);
  });

  /**
   * 叫醒某對話正在睡的補收輪詢。
   * @param {string} chatId 對話 id
   * @returns {boolean} true 表示真的有一輪在睡並已被叫醒（呼叫端就不必另起一輪）
   */
  const wakeRecovery = (chatId) => {
    const wake = recoveryWakeByChatRef.current.get(chatId);
    if (!wake) return false;
    console.log('[補收] 提前喚醒睡眠中的輪詢', { chatId });
    wake();
    return true;
  };
  // 補收函式的最新版本（函式每次 render 重建，觸發效應只建立一次，需經 ref 呼叫到最新版）
  const recoverPendingReplyRef = useRef(null);

  // 「補收落地後把畫面帶到最新訊息」的兩段動作（AGUI-1082）。捲動相關的 ref 與函式都宣告在
  // 本檔下半部（容器 ref、spacer、syncBottomSpacer 等），補收流程卻在上半部，故比照
  // checkVisibilityRecoverRef 的做法用 ref 間接呼叫，由下方捲動區塊每次 render 指派最新閉包。
  //   shouldFollow(cid) — 這次落地是否該帶動捲動；必須在「落地之前」問，見其實作說明
  //   scrollToLatest()  — 落地之後把畫面捲到真實內容底部
  const recoveryScrollRef = useRef({ shouldFollow: () => false, scrollToLatest: () => {} });

  // 「目前檢視的對話」是否正在串流的 ref 鏡像，供只建立一次（deps 為空）的 reveal 計時器讀取，避免閉包過期。
  // 只有目前畫面上這個 chat 正在串流才算 active——背景對話的 delta 不應把當前畫面釘到底部。
  const streamingActiveRef = useRef(false);

  // 切換「檢視的對話」時：逐字動畫改追新對話。直接把已揭示長度快照成新對話目前已累積的完整文字，
  // 避免切回一個進行中的對話時從頭重播打字（背景對話在離開畫面時並未逐字揭示）。
  useEffect(() => {
    const cur = streamsRef.current.get(chatHistory.currentChatId) || null;
    const full = cur?.text ?? '';
    revealedLenRef.current = full.length;
    setRevealedText(full);
    streamingActiveRef.current = !!cur;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatHistory.currentChatId]);

  // 當前對話的串流內容變化時：維護 streamingActiveRef，並在「新一輪串流剛開始（目標為空字串）」時
  // 重置揭示進度，避免殘留上一輪的文字。切換對話造成的快照由上一個 effect 負責，這裡不重複處理。
  useEffect(() => {
    const cur = streams.get(chatHistory.currentChatId) || null;
    streamingActiveRef.current = !!cur;
    if (cur && cur.text === '') {
      revealedLenRef.current = 0;
      setRevealedText('');
    }
  }, [streams, chatHistory.currentChatId]);

  /**
   * 落地某對話的最終訊息並清除其等待狀態（串流緩衝 + loading）（change: unify-reply-completion-signal，
   * 從原本內嵌於下方揭示動畫 effect 的 {@code commitFinal} 抽出，供 REST 回應路徑（透過
   * {@code pendingFinalByChatRef} + 下方 reveal 計時器）與補收輪詢的 {@code completed} 分支共用，
   * 避免兩者各自實作幾乎相同的「寫入 → 成功清狀態／失敗標記」邏輯，是 PR#358 那類重複洞的根治點）。
   *
   * updateChatMessages 是 async（內部先 await IndexedDB 才真正更新狀態），必須等它完成後才清
   * stream/loading，否則會有「暫時泡泡已消失、正式訊息還沒寫入」的空檔造成畫面跳動。
   *
   * 刻意不讓回傳的 Promise reject（內部 catch 已吸收例外並完成對應收尾），改以
   * {@code {ok: boolean}} 表示結果：呼叫端若不關心結果可以不接（如下方 reveal 計時器，fire-and-forget），
   * 需要額外收尾動作的呼叫端（如補收輪詢）可以安全 await 取得結果而不必自己重複 try/catch。
   *
   * @param {string} cid 對話 id
   * @param {Array} finalMessages 要寫入的訊息陣列
   * @param {object} [options] 透傳給 {@code updateChatMessages} 的選項（例如補收輪詢用的 {append: true}）
   * @returns {Promise<{ok: boolean, error?: unknown}>}
   */
  const landReplyMessages = useCallback((cid, finalMessages, options) => {
    pendingFinalByChatRef.current.delete(cid);
    awaitingOwnFinalizeByChatRef.current.delete(cid);
    removePendingClear(cid);
    return Promise.resolve(updateChatMessagesRef.current(cid, finalMessages, options)).then(() => {
      setStreamFor(cid, null);
      removeLoading(cid);
      // 回覆已落地 → 立即解除補收造成的等待鎖（isAwaitingReply 的 isRecovering 來源），
      // 不必等仍在輪詢的補收醒來；旗標讓那輪補收下次醒來時不再上鎖、直接收斂。
      replyLandedByChatRef.current.add(cid);
      removeRecovering(cid);
      clearLandingFailed(cid); // 這次落地成功，先前的失敗標記已無意義
      return { ok: true };
    }).catch((e) => {
      // 落地失敗（IndexedDB 讀寫被拒：私密模式、被封鎖、配額不足）也必須解除等待狀態：
      // 原本 then 不執行就等於 loading／串流永遠不清，輸入框永久鎖住且無任何線索可查。
      console.error('[落地] 最終訊息寫入失敗，仍解除等待狀態', { chatId: cid, error: e });
      setStreamFor(cid, null);
      removeLoading(cid);
      // isLoading 之外還要解 hasPendingGap 那條鎖：寫入若在「讀既有紀錄」就失敗，chatHistory
      // 沒被更新，本地陣列最後一則仍是未回覆的提問，只清 loading 的話輸入框依然打不了字。
      addLandingFailed(cid);
      return { ok: false, error: e };
    });
  }, [removePendingClear, setStreamFor, removeLoading, removeRecovering, clearLandingFailed, addLandingFailed]);

  useEffect(() => {
    const REVEAL_INTERVAL_MS = 25;
    const REVEAL_CHARS_PER_TICK = 4; // 固定速度，約每秒 160 字，不隨落後量變快變慢

    const timer = setInterval(() => {
      const currentId = currentChatIdRef.current;

      // (1) 背景對話（非目前檢視）的最終訊息：不做逐字動畫，直接落地。逐字動畫只服務當前對話，
      //     背景對話若也等動畫追完會永遠卡著（計時器不推進它），故一偵測到 pendingFinal 就即時落地。
      const pendingMap = pendingFinalByChatRef.current;
      if (pendingMap.size > 0) {
        for (const cid of Array.from(pendingMap.keys())) {
          if (cid === currentId) continue;
          landReplyMessages(cid, pendingMap.get(cid));
        }
      }

      // (2) 當前對話的逐字動畫。
      const target = streamsRef.current.get(currentId)?.text ?? '';
      const len = revealedLenRef.current;
      if (len < target.length) {
        const next = Math.min(len + REVEAL_CHARS_PER_TICK, target.length);
        revealedLenRef.current = next;
        setRevealedText(target.slice(0, next));
      }

      // (3) 當前對話揭示進度已追上目標值且有暫存待落地訊息 → 正式落地
      if (revealedLenRef.current >= target.length && pendingMap.has(currentId)) {
        landReplyMessages(currentId, pendingMap.get(currentId));
      }
    }, REVEAL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [landReplyMessages]);

  // ── AI 回覆補收（change: ai-response-server-persistence；改以聊天紀錄為唯一真相）──────
  // 手機切背景/WebView 被 OS 銷毀時，/send 的 HTTP 連線可能被砍斷，但後端會跑完並把完整回覆
  // 寫入暫存表。補收依據不再是獨立旗標，而是「聊天紀錄本身」（存於 IndexedDB，WebView 重建後仍在）：
  // 若當前對話最後一則是使用者提問、其後沒有回覆（見 findRecoverableReply），即透過
  // GET /web/chat/{chatId}/reply 三態查詢補收：
  //   completed  → 以「後端回傳的」assistantMessageId 落地本地歷史（append 模式，依 id 與正常路徑天然去重）
  //   processing → 退避輪詢（2s/4s/8s/16s/30s，本次輪詢上限 300s）
  //   not_found / 本次輪詢逾時 → 無法排除「後端其實還沒收到、或還在處理」（見 pendingReply.js
  //     檔頭「冷卻集合」說明），故不永久放棄，只是短暫冷卻（漸增退避），下次任一觸發點自動再查
  // 落地權：正常路徑與補收路徑皆以同一 assistantMessageId 落地，append 依 id 去重，重複落地天然無害。
  // forChatId：指定要補收的對話（不傳則預設「當前檢視的對話」）。
  // 由 sendMessage 的送出失敗/空回覆分支帶入 targetChatId，確保補收的是「送出的那個對話」——
  // 並發時送出回應到達時使用者可能已切到別的對話，不能用當前檢視對話來掃缺口（否則會補錯對話）。
  // opts.ignoreCooldown：只給觸發點2（visibility）與觸發點1（load，且限「登入完成後」那一次）用——
  // 兩者都是明確、低頻的使用者動作（切回前景／重新進站並完成登入），
  // 值得繞過 not_found／逾時留下的漸增退避冷卻、保證馬上真的查一次，不讓使用者誤以為訊息回不來了；
  // 自動觸發點（ws-reconnect/heartbeat）不可傳 true，否則冷卻機制形同虛設。
  const recoverPendingReply = async (reason, failText = null, forChatId = null, opts = {}) => {
    const { ignoreCooldown = false } = opts;
    const targetId = forChatId || chatHistory.currentChatId;
    // 取得指定對話的訊息：一律經 messagesForChatRef 取「最新」值（見其宣告處的 stale closure 說明）。
    // 首次掃描的行為不變；差別在輪詢迴圈中的後續呼叫能看到迴圈啟動之後才落地的回覆。
    const messagesForChat = (cid) => (messagesForChatRef.current ? messagesForChatRef.current(cid) : []);
    // 補收對象取「指定對話」的缺口（掃描聊天紀錄，而非讀取獨立旗標）
    const scan = () => findRecoverableReply(messagesForChat(targetId), targetId, { ignoreCooldown });
    const pending = scan();
    // 沒有缺口（答案已落地）與「已有另一輪在跑」分開回報：呼叫端只有後者需要稍後再試一次
    if (!pending) return 'no-gap';
    if (recoveryInFlightByChatRef.current.has(pending.chatId)) return 'skipped';
    recoveryInFlightByChatRef.current.add(pending.chatId);
    console.log(`[補收] 開始（${reason}）`, pending);

    // 補收落地後清理「該對話」的等待狀態（串流緩衝 + loading）。
    // 若該對話正好有一次真正送出仍在進行中（sendInFlightByChat），交給那次送出自己收尾，這裡不動
    //（避免誤清：曾發生切到新對話送出後，舊對話補收完成反而把新對話尚未回覆的等待清掉）。
    // 狀態已 per-chat，故只針對 pending.chatId 清理，不影響其他對話。
    const cleanupWaitingUi = () => {
      if (sendInFlightByChatRef.current.has(pending.chatId)) return;
      removeLoading(pending.chatId);
      setStreamFor(pending.chatId, null);
      awaitingOwnFinalizeByChatRef.current.delete(pending.chatId);
    };

    // 本次查無結果：not_found 或本次輪詢逾時都不代表「答案不存在」，只代表「這次沒查到」——
    // 可能是 /send 這個 request 還在網路中還沒送達後端（手機訊號差時常見）、或 GenAIus 回覆較久
    // 剛好卡在單次輪詢上限，兩者都可能在稍後自然解決。故不永久放棄（markAbandoned 僅保留給
    // 使用者主動停止這一種真正「不要這個答案了」的情境），改為短暫冷卻（markRetryLater，漸增退避），
    // 下次任一觸發點（回前景／重開對話／WS 重連）自動再查一次；也不落地「請重新發送」的錯誤訊息，
    // 因為系統本來就會自動重試，提示使用者重送反而可能造成同一問題重複發送。
    const failSoftly = async () => {
      // 正常送出仍在進行中：不判死。此時答案仍可能由存活的送出回應落地（如檔案分析較久），
      // 或送出失敗會走 _sendFailed 觸發正確補收。不進冷卻、不顯示錯誤，交由後續處理。
      if (sendInFlightByChatRef.current.has(pending.chatId)) {
        console.log('[補收] 送出仍在進行中，暫緩終局失敗');
        return;
      }
      markRetryLater(pending.userMessageId);
      console.warn('[補收] 本次查無結果，稍後自動重試（不視為永久放棄）', {
        chatId: pending.chatId,
        userMessageId: pending.userMessageId,
        failText,
      });
      cleanupWaitingUi();
    };

    // 終局時限（change: unify-reply-completion-signal Section 5，部署後修正：原本這個判斷放在
    // 迴圈最外層、每輪無條件檢查純粹的「經過多久」，結果連「/send 連線仍活著」
    // （sendInFlightByChatRef 有值，正是停止鍵還能按的原因）或「後端已明確回過 processing」
    // 這兩種有正面證據還在跑的情況也一起被判死——這正是實測回報「停止鍵還能按、卻同時顯示
    // 送達失敗」的根因。改為只在 not_found（後端從未給過任何正面確認）這個結果分支才判斷是否
    // 已超過終局時限，且沿用 failSoftly 既有的 sendInFlightByChatRef 保護。
    // 「已確認 processing」的情境完全不受本判斷影響，繼續交給既有 DEADLINE_MS(300s，見下方)/
    // 軟性冷卻機制處理——那是已經有明確證據支撐的「只是比較慢」情境（實測案例：檔案分析類提問
    // 遠超 180 秒才回覆，屬正常現象），不該被當成「可能沒送達」顯示重送按鈕。
    const TERMINAL_DEADLINE_MS = 180000;
    const failNotFound = async () => {
      if (sendInFlightByChatRef.current.has(pending.chatId)) {
        console.log('[補收] 送出仍在進行中，暫緩終局失敗');
        return;
      }
      const questionCreatedMs = pending.createdAt ? Date.parse(pending.createdAt) : NaN;
      const overTerminalDeadline = !Number.isNaN(questionCreatedMs)
        && Date.now() - questionCreatedMs > TERMINAL_DEADLINE_MS;
      if (overTerminalDeadline) {
        console.warn('[補收] not_found 且已超過終局時限，判定送達失敗並解鎖（非永久靜默重試）', {
          chatId: pending.chatId,
          userMessageId: pending.userMessageId,
          elapsedMs: Date.now() - questionCreatedMs,
        });
        // markAbandoned：讓 findRecoverableReply 之後永久排除這則，不再被 heartbeat 等觸發點無聲
        // 重試；markDeliveryFailed：跨 session 持久化，供重整/重開後仍能顯示失敗提示；
        // addDeliveryFailedMsg：React state，保證這一刻一定會 re-render（見其宣告處說明）。
        markAbandoned(pending.userMessageId);
        markDeliveryFailed(pending.userMessageId);
        addDeliveryFailedMsg(pending.userMessageId);
        cleanupWaitingUi();
        return;
      }
      markRetryLater(pending.userMessageId);
      console.warn('[補收] 本次查無結果，稍後自動重試（不視為永久放棄）', {
        chatId: pending.chatId,
        userMessageId: pending.userMessageId,
        failText,
      });
      cleanupWaitingUi();
    };

    // 第一次查詢一律立即打（見下方迴圈開頭），目的是盡快分辨「後端真的在跑」(processing)
    // 還是「後端根本沒收到/沒在跑」(not_found)，後者要盡快進入 pendingReply.js 的冷卻序列。
    // 確認是 processing 之後才會用到這裡的 backoff：GenAIus 生成已知至少要 20 秒以上，
    // 故前段刻意拉長到 15 秒才重試，避免在「絕對還沒好」的窗口內做注定白問的查詢；
    // 過了這個窗口後改為較密集的 5 秒一次，並將封頂從 30 秒降到 10 秒——
    // 降低「答案已寫進暫存表、但前端還在睡眠中沒去查」這段死區時間的最壞情況
    // （實測案例：答案 15:47:44 已就緒，前端因睡在 30 秒區間, 直到 15:48:09 才查到，多等 25 秒）。
    const BACKOFF_MS = [15000, 5000, 5000, 5000, 10000];
    // 輪詢死線對齊後端 send-message-timeout(300s)：慢速回覆（如檔案分析）在純輪詢時也不會被提前判死。
    // not_found 為即時失敗、不受此死線影響；processing／accepted（皆為「後端已收到、繼續等」）
    // 才受此上限約束，且逾時走的是軟性冷卻 failSoftly，不是判死。
    const DEADLINE_MS = 300000;
    const startedAt = Date.now();
    let attempt = 0;
    // 使用者已對這則提問按下停止：這一輪輪詢再查下去也永遠等不到回覆，立刻收工把
    // recoveryInFlightByChatRef 交還給同一對話的下一題（見該旗標宣告處說明）。
    const stopRequested = () => recoveryStopUserMsgIdsRef.current.has(pending.userMessageId);
    try {
      while (true) {
        // 迴圈起點檢查：涵蓋「睡在退避等待中被 wakeRecovery 叫醒」的情況
        if (stopRequested()) {
          console.log('[補收] 使用者已停止此提問，結束輪詢', { userMessageId: pending.userMessageId });
          return 'stopped';
        }
        let data;
        try {
          data = await apiService.getBufferedReply(pending.chatId, pending.userMessageId);
        } catch (e) {
          // 查詢本身失敗（斷網、認證等）：缺口仍在聊天紀錄裡，留待下一個觸發點重試，先解除等待畫面
          console.warn('[補收] 查詢失敗，待下次觸發重試', e);
          cleanupWaitingUi();
          return 'error';
        }

        // 查詢往返期間按下的停止：此時沒有人睡在 sleep 裡可被叫醒，若不在這裡檢查，
        // processing 分支會直接進入下一輪退避睡眠（最長 15 秒）才發現該停了
        if (stopRequested()) {
          console.log('[補收] 查詢期間使用者已停止此提問，結束輪詢', { userMessageId: pending.userMessageId });
          return 'stopped';
        }

        const status = data?.status;
        if (status === 'completed') {
          // 以後端回傳的 assistantMessageId 落地：與正常路徑落地的 id 相同，append 依 id 去重，
          // 即使正常路徑已搶先落地，這裡的 append 也會被過濾成 no-op，不會產生重複訊息。
          //
          // change: unify-reply-completion-signal——落地與收尾邏輯改呼叫與 REST 回應路徑共用的
          // landReplyMessages（見其宣告處），不再各自維護一份幾乎相同的 try/catch。這正是根治
          // PR#358 那類重複洞的地方：先前這裡若不接 catch，例外會讓 recoverPendingReply 直接
          // reject、跳過收尾，loading 永遠不會被清，等於重現「已修好」的永久鎖死，只是換了個入口
          // （補收落地，而非舊版 commitFinal 落地）——landReplyMessages 內部已保證兩種結果都收尾。
          // 捲動意圖必須在落地「之前」量測（AGUI-1082）：落地會清掉串流緩衝、加入新訊息並改變
          // spacer 高度，之後再問「使用者原本是否停在最新訊息旁」就已經失真。
          const followToLatest = recoveryScrollRef.current.shouldFollow(pending.chatId);
          // data.mcpApps（AGUI-1274）：本輪工具結果帶的 MCP Apps 互動畫面（例如簡報建立畫面）。
          // fast-accept 模式下這裡是 bot 訊息唯一的落地點，畫面標記只能從這裡掛上訊息。
          const landResult = await landReplyMessages(pending.chatId, [withMcpApps({
            id: data.assistantMessageId,
            sender: 'chatbot',
            text: data.replyText ?? '',
            createdAt: data.createdTime || new Date().toISOString(),
          }, data.mcpApps)], { append: true });
          if (landResult.ok) {
            console.log('[補收] 完成落地', { chatId: pending.chatId, assistantMessageId: data.assistantMessageId });
            // 補收落地的回覆是「使用者離開這段期間產生的新內容」，且這條路徑沒有串流（append 直接
            // 落地），既有的「串流從有到無」與 messageCount 捲動 effect 都不會動它（後者對
            // sender==='chatbot' 一律 return）→ 不在此補一次捲動，答案就會無聲長在畫面外，
            // 表現成「切掉 WebView 再進來沒有捲到最下面」（AGUI-1082）。
            if (followToLatest) recoveryScrollRef.current.scrollToLatest();
            clearRetryLater(pending.userMessageId); // 已成功補收，清掉冷卻紀錄避免集合殘留無用條目
            // landReplyMessages 已完成無條件解鎖與落地清理，此處只補上補收特有的旗標：
            // 記下這則提問已由補收落地，讓仍在進行中、稍後才回來的 /send 得知答案已由此處落地，
            // 不再重複落地（或用它出發時的舊訊息陣列覆蓋掉補收之後才產生的內容）。
            recoveryLandedUserMsgIdsRef.current.add(pending.userMessageId);
            return 'completed';
          }
          // 落地失敗：landReplyMessages 內部已完成對稱的失敗收尾（setStreamFor/removeLoading/
          // addLandingFailed），不呼叫 recoveryLandedUserMsgIdsRef——那代表「答案真的已經在畫面上」，
          // 這裡並沒有，若誤標會讓仍在跑的其他輪詢／遲到的 /send 誤判成已落地而放棄自己的落地嘗試。
          console.error('[補收] 完成落地時寫入失敗，仍解除等待狀態', { chatId: pending.chatId, error: landResult.error });
          return 'error';
        }
        // processing／accepted 都代表「後端已確認收到這則提問」，差別只在生成是否已經開始：
        // accepted＝/send 入口已寫入受理痕跡、尚未呼叫 GenAIus；processing＝已在生成中。
        // 兩者都必須繼續輪詢並維持等待畫面——這正是後端四態設計（design.md 決策 4）對 accepted 的
        // 定義（「前端繼續輪詢」）。
        // ⚠️ 本分支原本只認 processing，accepted 會掉進下方分支被當成「後端從沒收到」：
        // fast-accept 開啟後，送出後的第一次查詢是立刻打的，此時後端 async 執行緒常常還沒來得及
        // markProcessing，回的正是 accepted → 前端當場結束輪詢並進入冷卻，且只要當下距離提問已超過
        // 終局時限就直接誤判「這則訊息可能未成功送達」，但答案其實稍後就正常生成完成。
        // 這是 AGUI-1082 實測「切背景超過三分鐘回來必現送達失敗、按重送卻立刻拿到答案」的主因。
        if (status === 'processing' || status === 'accepted') {
          // 回覆已落地（正常路徑或前一輪補收）→ 不再上鎖，直接結束本輪輪詢。
          // 後端此時仍可能回 processing（buffer 尚未寫入、或 marker 仍在），但答案已在畫面上，
          // 繼續鎖住輸入只會重現「內容看起來已結束、輸入框卻打不了字」的症狀。
          if (replyLandedByChatRef.current.has(pending.chatId)) {
            console.log('[補收] 回覆已落地，停止輪詢', { chatId: pending.chatId });
            return 'consumed';
          }
          // 確認後端「仍在處理」才上鎖：重建該對話的等待畫面並鎖住其輸入。
          // 刻意不在查詢前樂觀上鎖——若首查即回 not_found，可避免畫面先鎖再閃成失敗。
          // 正常送出仍在進行中時不重複上鎖：該情境由該對話的 loading 主導等待畫面，且送出回應會自行落地，
          // 避免補收鎖在正常回覆已落地後、輪詢尚未結束的空檔多鎖住輸入一小段時間。
          if (!sendInFlightByChatRef.current.has(pending.chatId)) {
            addRecovering(pending.chatId);
          }
          const waitMs = BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)];
          attempt += 1;
          if (Date.now() - startedAt + waitMs > DEADLINE_MS) {
            console.warn('[補收] 輪詢逾時');
            await failSoftly();
            return 'timeout';
          }
          // 可被「回到前景」提前叫醒（見 recoveryWakeByChatRef 宣告處說明）：
          // 背景期間這個等待會被瀏覽器節流／暫停，不能只靠計時器自己醒
          await sleepUntilWakeOrTimeout(pending.chatId, waitMs);
          // 等待期間缺口可能已被正常路徑落地填補（最後一則不再是未回覆的 guest）→ 停止輪詢
          if (!scan()) return 'consumed';
          continue;
        }
        if (status === 'not_found') {
          // 後端明確回報查無此提問：completed／processing／accepted 三種痕跡皆未命中，代表後端
          // 從未給過任何正面確認，是終局時限（見 failNotFound）唯一適用的分支
          console.warn('[補收] 無結果', { status });
          await failNotFound();
          return 'not_found';
        }
        // 無法辨識的狀態：例如後端日後新增第五種狀態，或 /reply 回了業務錯誤形狀（401/403）導致
        // status 為 undefined。「看不懂這個回應」不等於「確定沒送到」，不可據此判死——原本這裡與
        // not_found 共用同一條路徑，任何非預期回應都會被當成確定遺失，是同一類誤判的溫床。
        // 一律只做軟性冷卻，等下一個觸發點重試。
        console.warn('[補收] 無法辨識的回應狀態，僅軟性冷卻不判死', { status });
        await failSoftly();
        return 'unknown';
      }
    } finally {
      // 停止旗標只服務「這一輪輪詢」：用完即清，避免同一 userMessageId 的殘留影響日後（重送等）情境
      recoveryStopUserMsgIdsRef.current.delete(pending.userMessageId);
      recoveryInFlightByChatRef.current.delete(pending.chatId);
      // 若本輪是在等待之外結束（completed／not_found／逾時），resolver 尚未被清掉，這裡保險移除
      recoveryWakeByChatRef.current.delete(pending.chatId);
      // 旗標只服務「這一輪輪詢」，用完即消費，避免殘留讓之後的補收該上鎖時不上鎖
      replyLandedByChatRef.current.delete(pending.chatId);
      // 本輪補收結束一律解鎖（completed／consumed／not_found／timeout 皆會走到這裡）：
      // 回覆已落地、缺口已被正常路徑填補、或已判定終局失敗，皆不應再鎖住輸入。
      // 只解鎖「這個對話」，不影響其他對話同時進行中的補收。
      removeRecovering(pending.chatId);
    }
  };
  recoverPendingReplyRef.current = recoverPendingReply;

  // 觸發點 1：頁面載入 / 切換對話後的「首次掃描」（重整、WebView 重建後恢復的主要路徑）。
  // 聊天紀錄由 IndexedDB 非同步載入，首次 render 時通常還是空的，且 visibilitychange 不會在初次載入觸發，
  // 因此不能只在 mount 掃一次；改為在「當前對話確立且訊息已載入」後，對每個開啟的對話做一次掃描。
  // loadScanDoneRef 確保同一對話只掃一次（避免每次新增訊息都重掃），切換對話時重置。
  useEffect(() => {
    loadScanDoneRef.current = false; // 切換對話：允許對新對話做一次載入掃描
  }, [chatHistory.currentChatId]);

  // 認證狀態一有變動就歸還掃描機會（第二刀，實測 UAT 仍要等十幾秒後找到的洞）。
  // 進站時 useAuth 的 checkAuthStatus 會先從 IndexedDB 把「上一個 session」還原回來並 setUser，
  // 因此 isAuthed 在 M+ 執行 logout() 之前就已經是 true——只用「isAuthed 為真才掃」擋不住這種
  // 殘留狀態：掃描會拿著舊 session 搶跑，把 loadScanDoneRef 用掉，接著 M+ 登出→登入走完後，
  // isAuthed 雖然又回到 true，旗標卻一路停在 true，於是掃描不再執行、掉回 15 秒心跳。
  // （若那次搶跑剛好趕在 logout() 之前、且舊 token 仍有效，/reply 會成功 → 這正是偶爾秒回的原因，
  //   同一個競態換了位置而已。）
  // 改為只要 isAuthed 變動就重設：true→false（登出）與 false→true（登入完成）都會歸還機會，
  // 讓真正的登入完成後一定還有一次掃描可用。與 authTransitioning 守衛互為表裡——前者預防搶跑，
  // 這裡是保險，即使其他登入方式（SSO/builtin）有類似時序也能兜住。
  useEffect(() => {
    loadScanDoneRef.current = false;
  }, [isAuthed]);

  const currentMsgCount = getCurrentMessages()?.length ?? 0;
  useEffect(() => {
    if (loadScanDoneRef.current) return;
    // 尚未認證（token 不在或已失效）：直接返回且**不設 loadScanDoneRef**，把唯一一次掃描機會留著。
    // 這是實測「切走很久再回到 WebView，有時秒回、有時要等數十秒」的根因：App 帶 M+ token 進站時
    // 走的是「先登出再登入」（App.js handleMplusLogin），而 useChat 是無條件呼叫的 hook——App 的
    // early return 只擋畫面、擋不住 effect，因此聊天紀錄從 IndexedDB 載完的時間點若早於新 token
    // 到位，這個掃描就會在「沒有 token」的當下跑掉：
    //   ・完全沒 token → apiService.getBufferedReply 直接 throw → 走「查詢失敗」分支
    //   ・舊 token 已失效且 refresh 也失敗 → 回應沒有 status → 走「無法辨識的狀態」分支 → 打 30 秒冷卻
    // 兩者都會消耗掉 loadScanDoneRef，之後只剩 15 秒心跳（且可能再被冷卻擋住）才會去查，
    // 但答案其實早就在後端。哪一種發生取決於兩條非同步流程的競態，故同樣的操作結果時快時慢。
    // isAuthed 進 deps 後，「未認證 → 已認證」的轉換會自然再跑一次本 effect，等於把「登入完成」
    // 當成補收觸發點——它是必然發生、時機明確、token 保證到位的事件，比 visibilitychange
    // （WebView 回報不可靠，見觸發點 2）可靠得多。
    if (!isAuthed) return;
    // 認證流程仍在進行中（M+ 的「先登出再登入」尚未走完）：此刻的 isAuthed 可能只是 checkAuthStatus
    // 還原的舊 session，掃描會拿著即將失效的 token 搶跑，白白消耗一次查詢。等流程走完再掃。
    if (authTransitioning) return;
    if (!chatHistory.currentChatId || currentMsgCount === 0) return; // 尚未載入完成
    // 正在送出：這則提問交給正常路徑處理，不要對「剛送出、仍在等待」的提問另啟補收（避免每次發問都多打一次 /reply）
    if (sendInFlightByChatRef.current.has(chatHistory.currentChatId)) return;
    loadScanDoneRef.current = true;
    // ignoreCooldown：授權理由與觸發點 2（回前景）完全相同——「使用者重新進站並完成登入」是明確、
    // 低頻的使用者動作，值得繞過前一輪補收留下的冷卻保證真的查一次；心跳／WS 重連仍受冷卻約束。
    if (findRecoverableReply(getCurrentMessages(), chatHistory.currentChatId, { ignoreCooldown: true })) {
      recoverPendingReplyRef.current?.('load', null, null, { ignoreCooldown: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatHistory.currentChatId, currentMsgCount, isAuthed, authTransitioning]);

  // 觸發點 2：頁面由背景回到前景（手機切 app 回來的主要路徑）。
  // 每次 render 都更新 checkVisibilityRecoverRef 為最新閉包（見上方宣告處說明），
  // DOM listener 本身只掛載一次、但呼叫的永遠是「當下最新」的檢查邏輯，避免 stale closure。
  // 改用 hasPendingGap（不看冷卻）判斷是否有缺口，並帶 ignoreCooldown: true 呼叫補收：
  // 使用者主動切回前景，值得繞過 not_found／逾時留下的漸增退避冷卻、保證馬上真的查一次，
  // 不讓使用者誤以為訊息回不來了；心跳等自動觸發點仍走原本的冷卻邏輯，不受影響。
  // 刻意不檢查 document.visibilityState：跟觸發點4心跳當年的發現一樣，包在 App 容器裡的
  // WebView 對 Page Visibility API 的回報不可靠（可能永遠不等於 'visible'），若拿來當守門條件，
  // 這整個立即查詢在手機上就會形同虛設、永遠不會執行。改成只要監聽到的事件本身有觸發
  // （visibilitychange 或 window focus，兩者機制不同、互為備援），就視為「可能回到前景」直接一試；
  // 就算誤判（例如背景切換誤觸發），最壞只是多打一次 /reply，靠下面的 5 秒節流與
  // recoveryInFlightByChatRef 擋掉洗版風險，不影響正確性。
  // 另加 5 秒節流（見 lastVisibilityRecoverAtByChatRef 宣告處說明），避免快速切換前景/背景洗版。
  checkVisibilityRecoverRef.current = () => {
    const chatId = chatHistory.currentChatId;
    if (!hasPendingGap(getCurrentMessages(), chatId)) return;
    const now = Date.now();
    const lastAt = lastVisibilityRecoverAtByChatRef.current.get(chatId) ?? 0;
    if (now - lastAt < VISIBILITY_RECOVER_THROTTLE_MS) return;
    lastVisibilityRecoverAtByChatRef.current.set(chatId, now);
    // 已有一輪補收睡在退避等待中：叫醒它就好，不另起一輪（那樣只會被 recoveryInFlight 擋成
    // 'skipped'，等於回到前景卻沒有任何人去查答案）。刻意只掛在這個觸發點——心跳每 15 秒一次，
    // 若也拿來喚醒等於把退避上限強制壓成 15 秒，改變了輪詢節奏。
    if (wakeRecovery(chatId)) return;
    recoverPendingReplyRef.current?.('visibility', null, null, { ignoreCooldown: true });
  };
  useEffect(() => {
    const onForegroundSignal = () => checkVisibilityRecoverRef.current?.();
    document.addEventListener('visibilitychange', onForegroundSignal);
    window.addEventListener('focus', onForegroundSignal);
    return () => {
      document.removeEventListener('visibilitychange', onForegroundSignal);
      window.removeEventListener('focus', onForegroundSignal);
    };
  }, []);

  // 觸發點 3：WebSocket 重連成功（連線恢復代表網路已復原，適合立即補收）
  useEffect(() => {
    if (wsConnected && findRecoverableReply(getCurrentMessages(), chatHistory.currentChatId)) {
      recoverPendingReplyRef.current?.('ws-reconnect');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsConnected]);

  // 觸發點 4：心跳式主動重試。前三個觸發點都是「離散事件」——若補收剛好卡在冷卻期
  // （pendingReply.js 的 markRetryLater）查無結果，而使用者之後只是留在畫面上乾等、不觸發任何
  // mount/visibilitychange/ws-reconnect，就永遠沒有東西會在冷卻到期後自動再檢查一次：即使後端
  // 早已把答案寫進暫存表，畫面也會一直沒反應（實測手機發現的真因）。此心跳只負責「定期喚醒」，
  // 冷卻/墓碑判斷仍完全交給 findRecoverableReply（未到期會被 isRetryingTooSoon 擋掉，不會真的打 server）。
  // 刻意不判斷 document.visibilityState：包在 App 容器裡的 WebView 對 Page Visibility API 的
  // 回報不可靠（實測發現心跳因此被永遠擋掉、形同虛設，答案早就在後端也不會被補收），真的在背景時
  // setInterval 本身就會被瀏覽器/OS 節流或暫停，不需要再疊一層判斷；就算誤跑，也只是白算一次
  // findRecoverableReply，冷卻檢查仍會擋掉不到期的請求，不會多打 server。
  const checkHeartbeatRecoverRef = useRef(null);
  checkHeartbeatRecoverRef.current = () => {
    if (findRecoverableReply(getCurrentMessages(), chatHistory.currentChatId)) {
      recoverPendingReplyRef.current?.('heartbeat');
    }
  };
  useEffect(() => {
    const HEARTBEAT_INTERVAL_MS = 15000;
    const timer = setInterval(() => checkHeartbeatRecoverRef.current?.(), HEARTBEAT_INTERVAL_MS);
    return () => clearInterval(timer);
  }, []);

  // 訊息容器（App.js 的 .messages 捲動區塊）的 ref，用於判斷使用者目前是否已停在底部
  const messagesContainerRef = useRef(null);
  // 判定「貼近底部」的容許誤差（px）
  const NEAR_BOTTOM_THRESHOLD_PX = 80;
  // 即時判斷目前是否在底部附近（每次呼叫都重新量測，不是送出當下的一次性快照）——
  // 比照 ChatGPT/Copilot 的做法：只有目前真的在底部附近才自動跟捲，使用者隨時手動往上捲都會立刻被尊重
  const isNearBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return true;
    return container.scrollHeight - container.scrollTop - container.clientHeight <= NEAR_BOTTOM_THRESHOLD_PX;
  }, []);

  // 目前檢視對話的串流緩衝（null 表示沒有）。以下捲動與泡泡渲染都只看「當前對話」的串流，
  // 背景對話的串流不影響當前畫面。
  const currentStream = streams.get(chatHistory.currentChatId) || null;
  // 對外暴露的 isLoading：目前檢視的對話是否在等待回覆。切到別的對話時即為 false（該對話各自獨立）。
  const isLoading = loadingChatIds.has(chatHistory.currentChatId);
  // 目前檢視的對話是否正在等後端確認停止（停止鈕顯示「停止中」並停用）
  const isStopping = stoppingChatIds.has(chatHistory.currentChatId);

  const baseMessages = getCurrentMessages();

  // 收尾瞬間：正式回覆已寫入 baseMessages，但串流緩衝（暫時泡泡）尚未清除。
  // 這是刻意的收尾順序（先落地、後清泡泡，避免中間出現兩者都不在的空檔），但 updateChatMessages
  // 在 flushSync 之後還有 await saveToStorage，瀏覽器來得及把這一格畫出來——若照常疊上暫時泡泡，
  // 畫面上會同時出現兩份相同的回覆，在提問置頂後的下方留白處閃一下。
  // 此時正式回覆已經在畫面上（不會有空檔），故這一格不再渲染暫時泡泡，等它被清除後即為穩定狀態。
  // 判斷依據：串流仍在進行中，但 baseMessages 的最後一則已經是 AI 回覆。
  const isFinalizeTransient =
    !!currentStream && baseMessages[baseMessages.length - 1]?.sender === 'chatbot';

  // 目前檢視的對話是否處於「已收到 CLEAR、等工具跑完與新內容送達」的空窗期。
  // 這段期間畫面留著呼叫工具前的過程文字（刻意不清空以免閃爍），但工具其實還在執行，
  // 需要一併顯示打字指示讓使用者知道還在處理中。
  const isToolProcessing = pendingClearChatIds.has(chatHistory.currentChatId);

  // 獲取當前訊息，串流中時疊加一則暫時的 bot 訊息泡泡供即時渲染（不寫入 chatHistory）
  const messagesWithStreamingBubble = (currentStream && !isFinalizeTransient)
    ? [...baseMessages, { id: '__streaming__', sender: 'chatbot', text: revealedText, isLoading: true, isStreaming: true, isProcessing: isToolProcessing, createdAt: currentStream.createdAt }]
    : baseMessages;
  // 標記終局送達失敗的提問（change: unify-reply-completion-signal Section 5）：deliveryFailedMsgIds
  // （React state，保證判定當下就 re-render）與 isDeliveryFailed（localStorage，跨 session 持久，
  // 涵蓋重整/重開後尚未觸發任何補收前的初始渲染）兩者皆可判定，只對真的被標記過的少數訊息
  // 建立新物件，其餘保留原參考不受影響。
  const messages = messagesWithStreamingBubble.map((m) => (
    m.sender === 'guest' && m.id && (deliveryFailedMsgIds.has(m.id) || isDeliveryFailed(m.id))
      ? { ...m, deliveryFailed: true }
      : m
  ));
  const messageCount = messages.length;
  const prevMessageCountRef = useRef(messageCount);

  // 偵測「串流從無到有」的邊界（而非依賴 messageCount）：這是使用者剛做的動作（送出訊息），
  // 不管原本捲到哪都直接捲到底部揭示。用邊界偵測而非 messageCount 的原因：
  // 使用者自己剛送出的訊息要透過 async 的 updateChatMessages 落地，有時會晚於暫時泡泡出現才真正
  // 反映到 baseMessages（也可能提早／同時），若用「messageCount 增加就捲、同一輪只捲一次」的方式，
  // 會在「使用者訊息晚到」時漏掉一次必要的捲動（畫面停在泡泡剛出現、還沒算入使用者訊息高度的位置）；
  // 而串流收尾時「正式訊息已寫入、暫時泡泡尚未清空」的短暫重複渲染，也會被誤判成新訊息。
  // 延遲一小段時間（遠大於 IndexedDB 讀寫耗時）才真正捲動，確保使用者訊息已經落地。
  const prevStreamingReplyPresentRef = useRef(false);
  useEffect(() => {
    const wasPresent = prevStreamingReplyPresentRef.current;
    // 只看目前檢視對話的串流：從無到有（使用者剛在此對話送出，或切到一個進行中的對話）才捲動
    const isPresent = !!currentStream;
    prevStreamingReplyPresentRef.current = isPresent;
    if (!wasPresent && isPresent) {
      const timer = setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
      }, 100);
      return () => clearTimeout(timer);
    }
    // 串流從有到無（本輪剛落地）：讚/倒讚按鈕只在訊息落地後才掛載，會讓泡泡在這一刻長高，
    // 但上面 tick-based 的跟捲（依賴 streamingActiveRef）已隨串流結束而停止，不會再補這次長高。
    // 只有使用者原本就在跟捲（貼近底部）時才補捲一次，避免使用者已手動往上捲時被強制拉回底部。
    if (wasPresent && !isPresent && isNearBottom()) {
      const timer = setTimeout(() => {
        if (isNearBottom()) {
          messagesEndRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [currentStream, isNearBottom]);

  // 最後一則訊息的來源，供下方捲動 effect 判斷是否為 AI 回覆（取純值而非整個陣列，
  // 避免把每次 render 都換新參考的 messages 放進 deps 造成 effect 每次 render 都跑）
  const lastMessageSender = messages[messageCount - 1]?.sender;

  // 僅在「有新訊息加入」時滾動到底部，避免在提問框輸入時畫面一直往下滑（尤其手機版）；
  // 串流中的捲動交給 scrollUserMessageToTop（見下方），這裡只處理非串流的新訊息（例如真人客服訊息）
  useEffect(() => {
    const prev = prevMessageCountRef.current;
    prevMessageCountRef.current = messageCount;
    if (messageCount <= prev) return;
    if (currentStream) return;
    // AI 回覆（sender: 'chatbot'）一律不在此處捲動：提問置頂後 scrollTop 恰好等於捲動極限，
    // isNearBottom() 會成立，若不排除，非串流路徑（如附件問答走 blocking）的回覆落地時
    // 會把 bot 回覆捲到頂端，蓋掉剛釘好的提問。真人客服（rocketChat）與通知訊息維持原行為。
    if (lastMessageSender === 'chatbot') return;

    if (!isNearBottom()) return;
    if (lastBotMessageRef.current) {
      lastBotMessageRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    } else if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messageCount, currentStream, lastMessageSender, isNearBottom]);

  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  // 切到 MyBro 畫面（App 掛載後選到預設對話，等同登入/webview 重新進入）或點擊歷史列表
  // 切換對話時，比照 ChatGPT/Grok 直接定位到最底部（最新訊息），而非停在瀏覽器預設的頂端位置。
  // 以 chatId 為 key 只做一次，避免同一對話後續新增訊息（例如發問置頂、串流跟捲）被這裡誤觸發
  // 而蓋掉那些既有捲動行為。刻意不監聽 visibilitychange/focus：單純切到背景再切回來不應改變
  // 使用者目前的捲動位置。
  // 用 useLayoutEffect 而非一般 effect：必須贏過下面 syncBottomSpacer 那個 layout effect
  // 的 race——若晚於它才捲，會捲進它剛算出來、為「把提問推到頂端」用的大片留白裡，
  // 畫面停在空白處，看起來像沒捲到最後一則訊息。
  // 只有「容器已掛載且訊息已載入」才視為真正完成並記住 chatId；currentMsgCount === 0 或
  // 容器尚不存在時都不標記，讓之後的重試（下面兩個觸發點之一）還有機會補上。
  //
  // 捲動方式（AGUI-1178）：改成「貼底意圖」而非單次捲動。快速點擊長對話時，切換後高度會持續
  // 變動（圖片／表格載入、手機側欄收起、網址列收合），先前「切換當下捲一次」與「看 3 幀」兩版
  // 都因為之後高度又變而停在頂端。現在切換後由 ResizeObserver 監看，高度一變就補捲到內容底部，
  // 直到高度穩定、使用者自己動手捲、送出新提問或超過時間上限才解除（見 utils/stickToBottom.js）。
  //
  // 先排好再顯示（AGUI-1302）：對話裡有可操作的互動畫面（MCP Apps iframe）時，貼底期間先把訊息區
  // 隱藏（isPositioning），等貼底結束、iframe 就緒才顯示。手指落在 iframe 上滑動時外層收不到觸控事件，
  // 貼底分不出使用者在往上捲而一直拉回底部；隱藏期間使用者碰不到訊息區，就不會有這個衝突。
  // 純文字對話維持原本行為（立即可見），不多等。見 utils/revealAfterPositioning.js。
  const initialBottomScrollDoneChatIdRef = useRef(null);
  // 目前生效中的貼底意圖（若有）：{ chatId, stop, reveal }，供切換到別的對話、送出新提問時解除；
  // reveal 只在隱藏定位時才有值
  const stickToBottomRef = useRef(null);
  // 訊息區是否正在隱藏定位中（App.js 據此隱藏 .messages 並顯示載入提示）
  const [isPositioning, setIsPositioning] = useState(false);
  // 供 tryInitialBottomScroll 判斷是否有可操作互動畫面的最新訊息（放 ref 讓該 callback 維持穩定身分）
  const currentMessagesRef = useRef([]);
  currentMessagesRef.current = getCurrentMessages() || [];
  const releaseStickToBottom = useCallback(() => {
    const current = stickToBottomRef.current;
    stickToBottomRef.current = null;
    // 先中止顯示閘門（立刻顯示、不捲動），再停貼底；順序反過來的話貼底的 onStop 會讓閘門繼續往下走
    current?.reveal?.cancel();
    current?.stop();
  }, []);
  const tryInitialBottomScroll = useCallback((chatId, container, msgCount) => {
    if (!chatId || !container || msgCount === 0) return;
    if (initialBottomScrollDoneChatIdRef.current === chatId) return;
    // 上一個對話的貼底意圖還在：作廢，不覆蓋新對話的捲動狀態
    releaseStickToBottom();
    initialBottomScrollDoneChatIdRef.current = chatId;

    // baselineTop：程式最後一次把畫面捲到的位置，供結束後的補捲檢查判斷使用者有沒有自己往上捲
    const entry = { chatId, stop: null, reveal: null, baselineTop: null };
    stickToBottomRef.current = entry;
    const startedAt = Date.now();
    // 終點要扣掉底部留白（spacer），否則會捲進為提問置頂而撐出的空白區
    const scrollToEnd = () => {
      const spacerHeight = bottomSpacerRef.current?.offsetHeight ?? 0;
      container.scrollTop = Math.max(0, container.scrollHeight - spacerHeight - container.clientHeight);
    };
    if (hasInteractiveMcpApp(currentMessagesRef.current)) {
      setIsPositioning(true);
      entry.reveal = startRevealGate(container, {
        scrollToEnd,
        onReveal: (reason, elapsedMs) => {
          // 閘門結束前已做最後一次捲到底：以此為基準，之後若往上移就是使用者自己捲的
          entry.baselineTop = container.scrollTop;
          setIsPositioning(false);
          logger.info('聊天', '切換對話隱藏定位結束', { chatId, reason, elapsedMs });
        },
      });
    }
    entry.stop = startStickToBottom(container, {
      scrollToEnd,
      // 貼底期間被別的邏輯拉離底部時，由 stickToBottom 立刻拉回
      isAtEnd: () => {
        const spacerHeight = bottomSpacerRef.current?.offsetHeight ?? 0;
        return container.scrollHeight - spacerHeight - container.scrollTop - container.clientHeight <= 2;
      },
      onStop: (reason) => {
        // 隱藏定位中：通知閘門貼底已結束，由它決定何時顯示（已中止時為 no-op）
        entry.reveal?.stickDone();
        if (entry.baselineTop === null) entry.baselineTop = container.scrollTop;
        if (stickToBottomRef.current === entry) stickToBottomRef.current = null;
        const snapshot = () => {
          const spacerHeight = bottomSpacerRef.current?.offsetHeight ?? 0;
          return {
            chatId, msgCount, reason, elapsedMs: Date.now() - startedAt,
            scrollTop: container.scrollTop, scrollHeight: container.scrollHeight,
            clientHeight: container.clientHeight, spacerHeight,
            distanceToBottom: container.scrollHeight - spacerHeight - container.scrollTop - container.clientHeight,
          };
        };
        logger.info('聊天', '切換對話貼底結束', snapshot());
        // 使用者自己動手捲就不算失敗。其餘結束原因（穩定／逾時／被手動解除）之後若仍不在底部，
        // 代表貼底完成後又有別的東西把畫面拉走，或貼底本身沒生效；此問題偶發、只在手機快速點擊出現，
        // 無法靠 console 追，故失敗時才送 Sentry（logger.error），正常情況不產生任何事件。
        if (reason === 'user') return;
        setTimeout(() => {
          // 已切到別的對話、或容器已卸載：不是這一輪的結果，不判定
          if (currentChatIdRef.current !== chatId || !container.isConnected) return;
          // 貼底開始後使用者碰過畫面（例如剛切換完就往上滑）：絕不補捲、也不上報
          if (entry.stop?.hasUserInteracted?.()) return;
          // 手指落在 iframe（MCP 互動畫面）上往上滑時，外層收不到任何手勢事件，上面那行抓不到；
          // 改看捲動位置：比基準往上移超過門檻就是使用者自己捲的，不補捲也不上報（AGUI-1302）
          if (hasScrolledUpFrom(container.scrollTop, entry.baselineTop)) return;
          const before = snapshot();
          const missed = (snap) => snap.scrollHeight - snap.spacerHeight > snap.clientHeight && snap.distanceToBottom > NEAR_BOTTOM_THRESHOLD_PX;
          if (!missed(before)) return;
          // 貼底結束後又被拉走：先補捲一次；補完仍不在底部才算失敗並上報
          container.scrollTop = Math.max(0, container.scrollHeight - before.spacerHeight - container.clientHeight);
          const after = snapshot();
          logger.warn('聊天', '貼底結束後被拉離底部，已補捲', { before, after });
          if (missed(after)) {
            logger.error('聊天', '切換對話後未停在底部(AGUI-1178)', { ...after, before, isMobile });
          }
        }, 1500);
      },
    });
  }, [releaseStickToBottom, isMobile]);

  // 觸發點 A：容器已經掛載時（一般情況），對話切換或訊息載入完成後捲到底部。
  // 每次切換對話（chatId 改變）都清掉「已捲過」記號，讓「每次點選歷史對話都一定捲到底」：
  // 否則 A → 新對話（訊息數 0，tryInitialBottomScroll 直接 return、不會清記號）→ 再點 A 時，
  // 記號仍是 A 而被擋下，完全不捲、停在新對話留下的頂端位置（AGUI-1178 偶發停頂的一個成因）。
  const lastSeenChatIdRef = useRef(null);
  useLayoutEffect(() => {
    if (lastSeenChatIdRef.current !== chatHistory.currentChatId) {
      lastSeenChatIdRef.current = chatHistory.currentChatId;
      initialBottomScrollDoneChatIdRef.current = null;
      // 前一個對話的貼底／隱藏定位還在跑：立刻解除。否則切到訊息數 0 的新對話時
      // tryInitialBottomScroll 會直接 return，訊息區要等前一個對話的閘門逾時才會顯示（AGUI-1302）。
      // 同一個 chatId 的貼底（例如觸發點 B 剛啟動的）不受影響。
      if (stickToBottomRef.current && stickToBottomRef.current.chatId !== chatHistory.currentChatId) {
        releaseStickToBottom();
      }
    }
    tryInitialBottomScroll(chatHistory.currentChatId, messagesContainerRef.current, currentMsgCount);
  }, [chatHistory.currentChatId, currentMsgCount, tryInitialBottomScroll, releaseStickToBottom]);

  // 供下面 callback ref 取用的「最新 chatId／訊息數」。放進 ref 是為了讓那個 callback ref
  // 能保持穩定的函式身分（見下方說明），不因每次新訊息就換一個新函式。
  const latestInitialScrollCtxRef = useRef(null);
  latestInitialScrollCtxRef.current = { chatId: chatHistory.currentChatId, msgCount: currentMsgCount };

  // 觸發點 B：容器「晚掛載」的情境——例如 M+ 登入流程要等 auth/網路檢查等好幾個 App.js
  // 自己的 loading flag 都跑完才會顯示聊天畫面，但 chatHistory 可能早已選好對話、訊息也載入
  // 完成，觸發點 A 執行的當下 messagesContainerRef.current 還是 null（不會標記完成，見上）；
  // 之後 chatId／訊息數不再變化，觸發點 A 的 effect 依賴不會再觸發，永遠補不回這次捲動。
  // 改用 callback ref：DOM 節點實際掛載那一刻一定會被呼叫到，不受那些其他 loading flag 影響。
  //
  // 這個 callback 必須維持穩定身分（deps 只有同樣穩定的 tryInitialBottomScroll，最新值改從
  // latestInitialScrollCtxRef 讀）：callback ref 只要函式身分變了，React 就會先以 null 卸下、
  // 再以節點掛上一次。若身分每次新訊息都變，下面「node 為 null 就重置標記」的判斷會被這種
  // 假卸載觸發，導致每來一則新訊息都重新捲到底部，蓋掉發問置頂與使用者自行往上捲的位置。
  const setMessagesContainerRef = useCallback((node) => {
    messagesContainerRef.current = node;
    if (!node) {
      // 容器真正卸載：App.js 只有在 auth／網路關卡畫面（載入中、登入中、登入頁、錯誤頁）才會
      // 不渲染聊天區，對話進行中不會發生。登出時 useChat 本身不會卸載（它在 App.js 的早期
      // return 之上），標記若不清掉，重新登入還原到同一個對話時會被 chatId 相同的判斷擋掉、
      // 停在登出前的位置捲不到底。
      initialBottomScrollDoneChatIdRef.current = null;
      releaseStickToBottom();
      return;
    }
    const { chatId, msgCount } = latestInitialScrollCtxRef.current;
    tryInitialBottomScroll(chatId, node, msgCount);
  }, [tryInitialBottomScroll, releaseStickToBottom]);

  // 底部彈性留白（spacer）的 DOM ref（由 App.js 掛在 .messages 內容尾端）。
  // 為什麼需要它：捲動容器最多只能捲到 scrollHeight - clientHeight。剛送出第二題時，該題底下
  // 只有「提問本身 + 小小的等待泡泡」，總高度遠小於可視高度，容器根本沒有足夠餘裕把提問推到頂端，
  // 於是捲動停在極限（畫面中段），看起來就像「沒捲上去」——Gemini/ChatGPT 同樣是靠底部留白解決。
  const bottomSpacerRef = useRef(null);

  // 依「最新提問到內容結尾的距離」動態調整留白高度，補足讓該提問置頂所缺的餘裕。
  // 回覆越長需要的留白越少，長到超過一個畫面時自動歸零，因此不會留下永久空白。
  // 先扣掉 spacer 目前佔用的高度再計算，讓此函式可重複呼叫且結果穩定（idempotent）。
  const syncBottomSpacer = useCallback(() => {
    const container = messagesContainerRef.current;
    const spacer = bottomSpacerRef.current;
    if (!container || !spacer) return;
    const userEl = lastUserMessageRef.current;
    if (!userEl) {
      if (spacer.style.height !== '0px') spacer.style.height = '0px';
      return;
    }
    const contentHeight = container.scrollHeight - spacer.offsetHeight;
    const needed = Math.max(0, container.clientHeight - (contentHeight - userEl.offsetTop));
    const next = `${needed}px`;
    if (spacer.style.height !== next) spacer.style.height = next;
  }, []);

  // ── 補收落地後回到最新訊息（AGUI-1082）─────────────────────────────────────────────
  // 為什麼不能沿用上面的 isNearBottom：它拿 scrollHeight 直接算距離，而 scrollHeight 含底部
  // 彈性留白（spacer）。進站捲底後 syncBottomSpacer 會為了「提問置頂」撐出接近一整屏的留白，
  // 此時使用者視覺上明明就停在最後一則訊息旁，isNearBottom 卻因為那片留白判成「離底部很遠」。
  // 判斷「使用者是否在跟著看最新訊息」必須只看真實內容，故這裡把 spacer 高度扣掉。
  // 刻意不改 isNearBottom 本身：它服務的是送出置頂與串流收尾，行為維持原樣不受本次修正影響。
  const isNearContentBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return false; // 聊天區還沒掛載，沒有畫面可捲
    const spacerHeight = bottomSpacerRef.current?.offsetHeight ?? 0;
    const distance = container.scrollHeight - spacerHeight - container.scrollTop - container.clientHeight;
    return distance <= NEAR_BOTTOM_THRESHOLD_PX;
  }, []);

  // 捲到「真實內容」的底部：終點同樣要扣掉 spacer，否則會捲進那片為提問置頂而留的空白裡，
  // 畫面停在空無一物的區域，看起來像沒捲到最後一則訊息（與 tryInitialBottomScroll 的坑相同）。
  const scrollToContentBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const spacerHeight = bottomSpacerRef.current?.offsetHeight ?? 0;
    container.scrollTop = Math.max(0, container.scrollHeight - spacerHeight - container.clientHeight);
  }, []);

  // 每次 render 指派最新閉包供上方補收流程經 recoveryScrollRef 呼叫（見其宣告處說明）
  recoveryScrollRef.current = {
    // 這次補收落地是否該帶動畫面。三個條件：
    //   1. 落地的就是使用者正在看的對話（背景對話落地不得動當前畫面）
    //   2. 該對話沒有串流緩衝——有串流代表這是本 session 自己送出、還在等待中的那一題，
    //      捲動歸「提問置頂 + 串流收尾」那套邏輯負責（例如快速受理 ack 也是走補收落地），
    //      在此插手會把剛釘好的提問拉走
    //   3. 落地前使用者仍停在最新訊息旁——已自行往上翻看歷史時不得強行拉回底部
    shouldFollow: (cid) => {
      if (!cid || cid !== currentChatIdRef.current) return false;
      if (streamsRef.current.get(cid)) return false;
      return isNearContentBottom();
    },
    // 落地後的捲動。延遲一格再做：landReplyMessages 的 promise resolve 時新訊息的 DOM 節點
    // 未必已經畫出來，且 spacer 要等 layout effect 依新內容重算才會縮回去；先自己同步一次
    // spacer 再捲，確保終點是「最後一則訊息的結尾貼齊視窗底部」。
    scrollToLatest: () => {
      setTimeout(() => {
        syncBottomSpacer();
        scrollToContentBottom();
      }, 100);
    },
  };

  // 捲到「使用者剛送出的問題」置頂（比照 Gemini/Copilot），取代原本送出後捲到底部：
  // 讓使用者發問後穩定看到自己的問題釘在畫面上方，不會被逐漸長高的 AI 回覆往下推出視野。
  // 直接操作容器的 scrollTop（而非 scrollIntoView）：位置由我們自己算，不會被瀏覽器
  // 對「捲動極限」的裁切或對祖先容器的連帶捲動影響。
  const scrollUserMessageToTop = useCallback(() => {
    const container = messagesContainerRef.current;
    const userEl = lastUserMessageRef.current;
    if (!container || !userEl) return;
    // 必須先補足底部留白，否則容器捲不到足以讓提問置頂的位置
    syncBottomSpacer();
    container.scrollTo({ top: userEl.offsetTop, behavior: 'smooth' });
  }, [syncBottomSpacer]);

  // 本對話目前最後一則使用者訊息的 id（找不到則為 null）；串流佔位泡泡 sender 是 'chatbot'，
  // 這裡的迴圈自然會略過，跟 App.js 找 lastGuestMessageIdx 掛 ref 用的是同一套邏輯。
  let lastGuestMessageId = null;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].sender === 'guest') {
      lastGuestMessageId = messages[i].id;
      break;
    }
  }

  // 偵測「本對話出現一則新的使用者訊息」才捲動，而非用固定延遲賭 updateChatMessages 何時落地——
  // updateChatMessages 是 async，內部要先 await 兩次 IndexedDB 讀取才真正 setChatHistory，
  // 耗時不固定，用 isLoading 變 true 當下就設 timer 常常撲空（訊息 DOM 節點還沒渲染出來，
  // lastUserMessageRef.current 還指向上一則訊息或 null）。改成看訊息 id 是否變化，才能保證
  // scrollUserMessageToTop 執行時該訊息一定已經在畫面上。切換對話（currentChatId 改變）
  // 視為基準重置、不觸發捲動，避免切到別的對話時把原本就存在的最後一則使用者訊息誤判成新提問。
  const guestMessageTrackRef = useRef({ chatId: chatHistory.currentChatId, guestId: lastGuestMessageId });
  useEffect(() => {
    const prev = guestMessageTrackRef.current;
    const isNewQuestionInSameChat =
      prev.chatId === chatHistory.currentChatId &&
      !!lastGuestMessageId &&
      lastGuestMessageId !== prev.guestId;
    guestMessageTrackRef.current = { chatId: chatHistory.currentChatId, guestId: lastGuestMessageId };
    if (!isNewQuestionInSameChat) return;
    // 使用者剛送出新提問：解除切換對話的貼底意圖，避免它把剛要置頂的提問又拉回底部
    releaseStickToBottom();
    // mobile 延遲較長，給虛擬鍵盤收起的版面高度變動動畫一點緩衝；PC 沿用原本 100ms
    const delay = isMobile ? 300 : 100;
    const timer = setTimeout(scrollUserMessageToTop, delay);
    return () => clearTimeout(timer);
  }, [lastGuestMessageId, chatHistory.currentChatId, isMobile, scrollUserMessageToTop, releaseStickToBottom]);

  // 回覆逐字長出、訊息落地、附件圖片載入等造成內容高度變化時，同步留白高度。
  // 只改留白、不做任何捲動，因此不違反「串流中不強制跟捲」；且公式保證「最新提問的位置」
  // 永遠仍落在可捲範圍內，已置頂的畫面不會因留白縮小被瀏覽器裁切 scrollTop 而往上跳。
  // 用 layout effect 是為了在瀏覽器繪製前就調整完，避免留白高度變化被看見。
  useLayoutEffect(() => {
    syncBottomSpacer();
  }, [revealedText, messageCount, isLoading, recoveringChatIds, isFinalizeTransient, syncBottomSpacer]);

  // 視窗尺寸變化（桌機拉視窗、手機轉向或鍵盤收放）會改變 clientHeight，需重算留白
  useEffect(() => {
    const onResize = () => syncBottomSpacer();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [syncBottomSpacer]);

  const sendMessage = async (value, options = {}) => {
    // options.forceNewChat（選填，change: notification-deeplink-chat-trigger）：
    // 不論目前開著哪個對話，一律強制走「建立新對話」分支（跟 currentChatId 現況無關），
    // 供推播深連結消費後自動開新對話送出用；一般打字送出不會帶這個 flag。
    // options.sourceChannel（選填，同一 change）：對話來源管道，一路帶到後端 /send 請求，
    // 僅在對話首次建立時由後端寫入 ag_tb_conversation.source_channel，一般送訊息不會帶。
    // options.onDispatched（選填，AGUI-1274 MCP Apps 的 ui/message）：(dispatched: boolean) => void。
    // sendMessage 本身會 await 到 AI 回覆落地為止，而互動畫面只等 10 秒、需要「訊息已送出」就立刻得知，
    // 故以回呼通知：通過所有守衛、提問已放進對話並開始送出時呼叫 true；在那之前任何一個靜默放棄的
    // early return（空字串、建立對話失敗、缺 chatId、同一對話仍在等待…）呼叫 false。只會呼叫一次。
    // options.preserveInput（選填，同一 change）：不清空輸入框。訊息不是從輸入框來的（互動畫面代送）時，
    // 使用者在輸入框打到一半的草稿不該被清掉。
    // 兩者皆不傳時行為與原本完全相同。
    const { forceNewChat = false, sourceChannel = null, onDispatched = null, preserveInput = false } = options;
    let dispatchNotified = false;
    const notifyDispatched = (dispatched) => {
      if (dispatchNotified || typeof onDispatched !== 'function') return;
      dispatchNotified = true;
      try {
        onDispatched(dispatched);
      } catch (e) {
        console.warn('[送出] onDispatched 回呼失敗', e);
      }
    };
    // value 可為三種格式：
    //   { files, attachmentUrls } — eager upload 已完成（正常流程）
    //   File[]                    — 舊式帶附件發送（相容保留）
    //   string / null             — 純文字或 starter pill
    // isEagerUpload: { fileData: [{ file, serverPath|null }] }
    const isEagerUpload =
      value !== null &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      'fileData' in value;

    const fileData = isEagerUpload ? (value.fileData || []) : null;
    const pendingFiles = isEagerUpload
      ? fileData.map((d) => d.file)
      : Array.isArray(value) ? value : [];

    const isUsablePrimitive =
      !isEagerUpload &&
      !Array.isArray(value) &&
      (value === null || ['string', 'number', 'boolean'].includes(typeof value));

    const messageToSend = (value !== undefined && isUsablePrimitive)
      ? String(value).trim()
      : input.trim();

    if (!messageToSend) {
      notifyDispatched(false);
      return;
    }
    // 防止重複發送的守衛移到 targetChatId 確定之後（見下方），改為「只擋同一個對話」，
    // 讓別的對話仍可在此對話等待期間獨立送出。

    // 確定要使用的聊天 ID 和當前訊息
    let targetChatId = chatHistory.currentChatId;
    let isNewlyCreated = false;
    let createdTemporaryChat = null;
    
    // 只有在 currentChatId 為 null 且 isNewChat 為 true 時，才創建新聊天
    // 如果 currentChatId 存在，即使 isNewChat 為 true（消息為空），也不應該再創建新聊天
    // forceNewChat 例外：不管目前 currentChatId 現況為何，一律強制建立新對話
    if (((!targetChatId && isNewChat) || forceNewChat) && createNewChat) {
      try {
        const newChat = await createNewChat();
        if (!newChat) {
          // 創建新聊天失敗，不發送訊息
          notifyDispatched(false);
          return;
        }
        // 使用新創建的聊天 ID（因為 React 狀態更新是異步的，chatHistory.currentChatId 可能還沒更新）
        targetChatId = newChat.id;
        isNewlyCreated = true;
        createdTemporaryChat = newChat;
      } catch (error) {
        console.error('創建新聊天失敗:', error);
        notifyDispatched(false);
        return;
      }
    }

    // 如果沒有有效的聊天 ID，無法發送訊息
    if (!targetChatId) {
      console.error('無法發送訊息：缺少聊天 ID');
      notifyDispatched(false);
      return;
    }

    // 若目前是臨時對話，延後到首次送出時才呼叫後端建立 chat_id
    const activeChatFromHistory = Array.isArray(chatHistory.chatHistory)
      ? chatHistory.chatHistory.find(chat => chat.id === targetChatId)
      : null;
    const activeChat = activeChatFromHistory || createdTemporaryChat;
    if (activeChat?.isTemporary) {
      try {
        // 若上傳檔案時已預先建立後端 session，直接重用，避免重複建立
        const preallocatedId = consumePreallocatedChatId?.();
        let serverChatId;
        if (preallocatedId) {
          serverChatId = preallocatedId;
        } else {
          const apiResponse = await apiService.createNewChatExternal();
          if (apiResponse === undefined) {
            notifyDispatched(false);
            return; // API 層已處理重新登入流程
          }
          serverChatId = apiResponse?.data?.data?.chat_id;
          if (!serverChatId) {
            throw new Error('初始化聊天室失敗：缺少 chat_id');
          }
        }
        if (typeof chatHistory.promoteTemporaryChat === 'function') {
          chatHistory.promoteTemporaryChat(activeChat.id, serverChatId, activeChat);
        }
        targetChatId = serverChatId;
      } catch (error) {
        console.error('初始化聊天室失敗:', error);
        notifyDispatched(false);
        return;
      }
    }

    // 防止重複發送：只擋「同一個對話」正在等待中的重送；別的對話不受影響（可並行送出）。
    if (loadingChatIdsRef.current.has(targetChatId)) {
      notifyDispatched(false);
      return;
    }

    // 如果剛創建了新聊天，使用空數組（新聊天初始為空）
    // 否則獲取當前訊息（因為 currentChatId 可能已改變）
    const currentMessages = isNewlyCreated ? [] : getCurrentMessages();
    // 僅在真人客服模式下才透過 WebSocket 發送；AI 對話一律走 REST（即使串流連線已開啟）
    const sendingViaWs = !!(isHumanMode && wsConnected && stompClient && wsSendDestination);
    // 立即建立本地預覽（blob URL）
    // eager upload：serverPath 已知且與 file 對應，直接帶入 serverUrl；舊式：serverUrl 待上傳後補
    const pendingAttachments = pendingFiles.map((f, i) => {
      const serverPath = fileData ? fileData[i]?.serverPath : null;
      return {
        name: f.name,
        type: f.type,
        previewUrl: URL.createObjectURL(f),
        serverUrl: serverPath
          ? `/bff/web/file/download?path=${encodeURIComponent(serverPath)}`
          : null,
      };
    });
    const userMessage = {
      sender: 'guest',
      text: messageToSend,
      isHuman: sendingViaWs,
      id: generateMessageId(),
      createdAt: new Date().toISOString(),
      ...(pendingAttachments.length > 0 ? { attachments: pendingAttachments } : {}),
    };
    const newMessages = [...currentMessages, userMessage];
    // 追蹤最新的 user message 列表（含 serverUrl）；上傳成功後會替換 blob URL，catch 區塊也需存取
    let resolvedMessages = newMessages;
    // 若最終訊息已交給 pendingFinalByChat 等（當前對話）揭示動畫追上後才落地，finally 就不該立刻清掉該對話狀態
    let deferredFinalize = false;

    // 僅更新本地／UI，不同步後端；等機器人回覆完畢後再一併同步
    updateChatMessages(targetChatId, newMessages);
    if (!preserveInput) setInput('');
    addLoading(targetChatId);
    // 記錄此對話「進行中的是哪一則提問」（供停止/終局失敗寫墓碑，以及下方 isStillOwner 判定）。
    // 刻意在 try 之前就設定：早期 return（真人客服走 WS、附件全部上傳失敗）也要能被 isStillOwner 認可，
    // 否則 finally 會誤判為「不是自己的」而不清除等待狀態，導致輸入框鎖住。
    inFlightUserMsgIdByChatRef.current.set(targetChatId, userMessage.id);
    // 新的一題開始等待：清掉上一題留下的「已落地」旗標，否則這一題的補收會誤判而不上鎖
    replyLandedByChatRef.current.delete(targetChatId);
    // 新的一題有自己的 isLoading 鎖，且缺口已屬於新那一題：清掉上一題留下的落地失敗標記，
    // 否則這個對話之後都不會因 gap 上鎖（可能讓使用者對同一 GenAIus session 併發送出第二題）
    clearLandingFailed(targetChatId);
    // 提問已放進對話、開始送出：通知呼叫端（不等 AI 生成，見 options.onDispatched 說明）
    notifyDispatched(true);

    // 這次送出是否仍是該對話「進行中的那一次」。
    // 送出相關狀態（等待中、AbortController、停止旗標等）都以 chatId 為 key，同一對話一次只服務一題；
    // 但 /send 的連線半開時這次送出可能遲到很久（見 apiService.request 的逾時說明），期間補收已把答案
    // 落地並解除等待狀態、使用者也送出了下一題——此時這些共用狀態已屬於新那一題，
    // 遲到的這次送出一律不得再碰，否則會清掉新那一題的等待狀態、或用過期的訊息陣列落地覆蓋掉新內容。
    const isStillOwner = () => inFlightUserMsgIdByChatRef.current.get(targetChatId) === userMessage.id;

    try {
      // 真人客服模式下，若 WebSocket 已連線且有目的地，改用 WS 發送
      if (isHumanMode && wsConnected && stompClient && wsSendDestination) {
        const payload = {
          chatId: targetChatId,
          message: messageToSend,
        };

        try {
          stompClient.publish({
            destination: wsSendDestination,
            body: JSON.stringify(payload),
          });
          // WebSocket 發送成功，但響應會通過訂閱接收，所以這裡不重置 loading
          // loading 狀態會在收到響應後由 useCustomerServiceSubscription 處理
          return;
        } catch (wsErr) {
          console.error('WebSocket 發送失敗，改用 API', wsErr);
          // fallback to API below
        }
      }

      // 若有待上傳的附件，取得 attachment URLs
      // eager upload 路徑：URLs 已由 useFileUpload hook 上傳完成，直接使用
      // 舊式路徑（File[]）：在此上傳（相容保留，正常流程不走此路）
      let attachments = isEagerUpload
        ? fileData.filter((d) => d.serverPath).map((d) => d.serverPath)
        : [];
      let uploadWarning = null;
      if (!isEagerUpload && pendingFiles.length > 0) {

        // 將成功上傳的 server paths 更新進 chat bubble，並更新 resolvedMessages
        const applyServerAttachments = (perFileResults) => {
          const bubbleAttachments = perFileResults.map((r, i) =>
            r.serverPath
              ? { name: pendingFiles[i].name, type: pendingFiles[i].type,
                  // previewUrl 保留 blob URL：<img> 不能帶 Authorization header，直接打 AI Agent 會破圖
                  previewUrl: pendingAttachments[i].previewUrl,
                  // serverUrl 走 BFF proxy → mygenieap 代理下載（由 mygenieap 加 API key）
                  serverUrl: `/bff/web/file/download?path=${encodeURIComponent(r.serverPath)}` }
              : pendingAttachments[i] // 失敗的保留 blob URL（圖片仍可預覽）
          );
          const updatedMessages = newMessages.map((msg) =>
            msg.id === userMessage.id ? { ...msg, attachments: bubbleAttachments } : msg
          );
          updateChatMessages(targetChatId, updatedMessages);
          // 保留帶 serverUrl 的版本，後續 baseMessages 使用此版本，避免 bot 回覆存檔時覆蓋 serverUrl
          resolvedMessages = updatedMessages;
          return bubbleAttachments;
        };

        try {
          // 先嘗試批次上傳（快速路徑）
          const uploadResult = await apiService.uploadFiles(pendingFiles, targetChatId);
          attachments = uploadResult?.attachments || [];
          if (attachments.length > 0) {
            applyServerAttachments(attachments.map((p) => ({ serverPath: p })));
          }
        } catch (batchError) {
          // 批次失敗：改為逐一上傳，蒐集成功的部分
          console.warn('批次上傳失敗，改為逐一上傳:', batchError);
          const results = await Promise.allSettled(
            pendingFiles.map((file) => apiService.uploadFiles([file], targetChatId))
          );
          const perFile = results.map((r) => ({
            serverPath: r.status === 'fulfilled' ? (r.value?.attachments?.[0] ?? null) : null,
          }));
          applyServerAttachments(perFile);
          attachments = perFile.map((r) => r.serverPath).filter(Boolean);

          const failCount = perFile.filter((r) => !r.serverPath).length;
          if (failCount === pendingFiles.length) {
            const serverMsg = results.find((r) => r.status === 'rejected')?.reason?.message;
            uploadWarning = `⚠️ 檔案上傳失敗${serverMsg ? `：${serverMsg}` : '，請檢視檔案格式是否正確。'}`;
          } else if (failCount > 0) {
            uploadWarning = `⚠️ ${failCount} 個檔案上傳失敗，已附帶其餘 ${pendingFiles.length - failCount} 個繼續發送。`;
          }
        }
      }

      // 上傳失敗時，在用戶訊息之後、AI 回覆之前插入一行警告
      const baseMessages = uploadWarning
        ? [...resolvedMessages, { sender: 'chatbot', text: uploadWarning }]
        : resolvedMessages;
      if (uploadWarning) {
        updateChatMessages(targetChatId, baseMessages);
      }

      // 有附件但全部上傳失敗 → 沒有任何附件可供 AI 參考，停止發送
      if (pendingFiles.length > 0 && attachments.length === 0) {
        return;
      }

      // 開啟串流暫存緩衝（若後端未啟用串流，AI_STREAM_DELTA 事件不會送達，文字維持空字串，
      // 畫面行為與現行「純 loading 動畫」一致，不影響非串流環境）
      // botCreatedAt 在這裡先固定下來，讓暫時泡泡跟稍後正式落地的訊息用同一個時間戳記，
      // 避免收尾那一刻才「長出」時間文字造成版面跳動
      const botCreatedAt = new Date().toISOString();
      setStreamFor(targetChatId, { text: '', createdAt: botCreatedAt });
      // 本對話自己送出的這則訊息，收尾時機交給下方揭示動畫/finally 決定，AI_STREAM_DONE 不要插手（見上方說明）
      awaitingOwnFinalizeByChatRef.current.add(targetChatId);
      // 只在等待 AI 回覆期間才建立 WebSocket 連線（節省 server 資源），回覆結束/停止後於 finally 關閉
      enableAi?.();

      // 為即將產生的 bot 回覆預生成 id，連同提問 id 一起帶給後端（change: ai-response-server-persistence）：
      // 後端據此暫存回覆供斷線補收，且正常路徑與補收路徑會以同一 id 落地（append/sync 皆依 id 去重）。
      // 補收依據改為「聊天紀錄中的未回覆提問缺口」，不再另存 pending 旗標（見 pendingReply.js）。
      const assistantMessageId = generateMessageId();

      const sendAbort = new AbortController();
      sendAbortByChatRef.current.set(targetChatId, sendAbort);
      // 標記此對話正常送出進行中：期間補收不得對它判死（await 涵蓋 GenAIus 生成的完整時間，含檔案分析）
      sendInFlightByChatRef.current.add(targetChatId);
      const response = await sendToGemini(messageToSend, currentMessages, targetChatId, attachments, sendAbort.signal,
        userMessage.id, { userMessageId: userMessage.id, assistantMessageId }, sourceChannel);
      // 快速受理 ack：/send 這個 HTTP 請求雖已回應，但 GenAIus 仍在後端背景生成，這次送出實際上
      // 還沒結束——旗標必須保留到下方 accepted-ack 補收輪詢跑完，統一交給 finally 清除。
      // 否則「送出仍在進行中不判死」（failSoftly／failNotFound 開頭的保護）只涵蓋 ack 的那幾百毫秒，
      // 整段生成期間完全失去保護：阻塞模式下這個旗標本來涵蓋整個生成期間，fast-accept 開啟後等於
      // 把 3dfe2eb 加的保護網架空，任何一次非 completed／processing 的查詢都能直接判死（AGUI-1082）。
      const isFastAcceptAck = typeof response === 'object' && response !== null && response._accepted;
      // 送出已有結果（成功或 _sendFailed）→ 解除進行中；後續 _sendFailed 補收才能正常判死。
      // 只有仍是「進行中的那一次」才動這些共用旗標，避免遲到的送出清掉使用者後來送出的下一題的狀態
      if (isStillOwner()) {
        if (!isFastAcceptAck) {
          sendInFlightByChatRef.current.delete(targetChatId);
        }
        sendAbortByChatRef.current.delete(targetChatId);
      }
      // 回覆真正抵達的時間點才取時間戳記：botCreatedAt 只服務送出當下的暫時泡泡動畫，
      // 正式落地的訊息要顯示 AI 實際回覆完成的時間，否則不論回覆花多久，畫面都會顯示成跟發送訊息同一秒
      const finalCreatedAt = new Date().toISOString();

      // 使用者已對「這一題」按停止，sendToGemini 回傳的內容（timeout/abort 錯誤字串）不應覆蓋停止
      // 系統訊息。判定對象是本次送出的提問，不是整個對話——否則停止會殃及之後送出的下一題
      // （見 stoppedUserMsgIdsRef 宣告處說明）。
      if (stoppedUserMsgIdsRef.current.has(userMessage.id)) return;

      // 已不是此對話進行中的那一次（補收先落地、輸入框已解鎖、使用者接著送出了下一題）：
      // 一律不落地任何內容，否則會用這次送出出發時的訊息陣列覆蓋掉新那一題的提問與回覆
      if (!isStillOwner()) {
        console.log('[送出] 已非此對話進行中的提問，跳過本次落地', { chatId: targetChatId, userMessageId: userMessage.id });
        return;
      }

      // 補收已搶先把這一題的答案落地（並已解除等待狀態）：這次遲到的送出不再落地任何內容，
      // 否則會用它出發時的訊息陣列覆蓋掉補收之後才產生的內容（例如使用者已送出的下一題）。
      if (recoveryLandedUserMsgIdsRef.current.has(userMessage.id)) {
        recoveryLandedUserMsgIdsRef.current.delete(userMessage.id);
        console.log('[送出] 答案已由補收落地，跳過本次落地', { chatId: targetChatId, userMessageId: userMessage.id });
        return;
      }

      // 快速受理 ack（change: unify-reply-completion-signal Section 4，僅
      // aiagent.send.async-accept-enabled=true 時後端才會回這個形狀）：/send 已受理但尚未有內容，
      // 不落地任何東西，改觸發一次補收查詢取得最終答案——複用 Section 1/2 已收斂好的
      // accepted→processing→completed 輪詢與單一落地函式 landReplyMessages，不新增第二套收尾邏輯。
      // await 這次呼叫直到真正落地：finally 的 enableAi/disableAi 生命週期因此維持與阻塞模式相同的
      // 語意（涵蓋整個等待期間），串流連線不會在 GenAIus 仍在後端生成時被提前關閉。
      // 這段期間 sendInFlightByChatRef 刻意仍保留（見上方 isFastAcceptAck 說明），由 finally 清除。
      if (isFastAcceptAck) {
        const ackResult = await recoverPendingReplyRef.current?.('accepted-ack', null, targetChatId);
        // 被擋成 'skipped' 代表同一對話另有一輪補收正握著旗標——最常見的來源是「上一題剛被停止、
        // 它的輪詢正在退出」的短暫交接窗口。這一題的補收若就此放棄，就沒有任何人會去拿它的答案
        // （後端照樣生成完畢並寫進暫存表，畫面卻空著）。故短暫等待後補打一次。
        if (ackResult === 'skipped') {
          await new Promise((resolve) => setTimeout(resolve, ACK_RECOVERY_RETRY_MS));
          await recoverPendingReplyRef.current?.('accepted-ack-retry', null, targetChatId);
        }
        return;
      }

      // 網路類發送失敗（連線被 OS 砍斷、逾時）：後端可能仍在處理或已完成暫存。
      // 若聊天紀錄中存在此提問的未回覆缺口 → 交給補收流程（可能已有另一觸發點的補收在進行，一律信任之，
      // 不在此另外落地錯誤文字以免與補收落地的真回覆重複）；否則退回把錯誤文字當作回覆落地。
      // 缺口判斷一律針對「這次送出的對話」（targetChatId）+ 本次送出的訊息陣列（resolvedMessages），
      // 不可用 getCurrentMessages()/currentChatId——並發時回應到達時使用者可能已切到別的對話，
      // 用當前檢視對話會掃錯對話，導致把錯誤訊息落到錯的對話或補收錯對話（檔案較慢時尤其明顯）。
      if (typeof response === 'object' && response._sendFailed) {
        if (findRecoverableReply(resolvedMessages, targetChatId)) {
          await recoverPendingReplyRef.current?.('send-failed', response.text, targetChatId);
          return;
        }
        // 把該對話串流目標清為空字串，讓逐字動畫視為「已追上」→ 下一 tick 立即落地錯誤文字（不逐字播放殘留）
        setStreamFor(targetChatId, (prev) => ({ text: '', createdAt: prev?.createdAt || botCreatedAt }));
        pendingFinalByChatRef.current.set(targetChatId, [...baseMessages, { sender: 'chatbot', text: response.text, createdAt: finalCreatedAt }]);
        deferredFinalize = true;
        return;
      }

      // 回覆內容為空：多半是同一 session 併發送出（切背景時連線被 OS 中斷、瀏覽器自動重送），
      // GenAIus 對第二個請求空手而回。空內容不可當答案落地（會顯示成一坨 JSON 或空泡泡）。
      // 有缺口則改走補收流程從後端暫存把真正的回覆補回；否則顯示友善訊息、不落地空內容。
      if (typeof response === 'object' && response._emptyReply) {
        if (findRecoverableReply(resolvedMessages, targetChatId)) {
          await recoverPendingReplyRef.current?.('empty-reply', null, targetChatId);
          return;
        }
        setStreamFor(targetChatId, (prev) => ({ text: '', createdAt: prev?.createdAt || botCreatedAt }));
        pendingFinalByChatRef.current.set(targetChatId, [...baseMessages, { sender: 'chatbot', text: '抱歉，這則回覆好像沒有成功產生，請重新發問一次。', createdAt: finalCreatedAt }]);
        deferredFinalize = true;
        return;
      }

      // 正常路徑落地：以預生成的 assistantMessageId 落地。若回前景觸發的補收已搶先落地同一 id，
      // 下方 updateChatMessages 的 append 會依 id 去重（見 useChatHistoryWithAdvancedSync），
      // 重複落地天然無害，不需額外的落地權判定。

      // 檢查是否包含圖表資料
      if (typeof response === 'object' && response.hasChart) {
        const botMessage = withMcpApps({
          // 預生成 id：與後端暫存列一致，補收/sync 依 id 收斂不重複
          id: assistantMessageId,
          sender: 'chatbot',
          text: response.text,
          hasChart: true,
          chartData: response.chartData,
          chartTitle: response.chartTitle,
          chartType: response.chartType,
          xlabel: response.xlabel,
          ylabel: response.ylabel,
          createdAt: finalCreatedAt,
        }, response.mcpApps);
        // 與下方純文字分支同理：交給 reveal 計時器在「訊息落地完成後」才清該對話串流，避免收尾瞬間的往上捲動
        pendingFinalByChatRef.current.set(targetChatId, [...baseMessages, botMessage]);
        deferredFinalize = true;
      }
      // 檢查是否包含轉接真人客服
      else if (typeof response === 'object' && response.hasTransferToHumanAgent) {
        const botMessage = withMcpApps({
          // 預生成 id：與後端暫存列一致，補收/sync 依 id 收斂不重複
          id: assistantMessageId,
          sender: 'chatbot',
          text: response.text || '正在為您轉接真人客服，請稍候...',
          hasTransferToHumanAgent: true,
          createdAt: finalCreatedAt,
        }, response.mcpApps);
        // 與下方純文字分支同理：交給 reveal 計時器在「訊息落地完成後」才清該對話串流，避免收尾瞬間的往上捲動
        pendingFinalByChatRef.current.set(targetChatId, [...baseMessages, botMessage]);
        deferredFinalize = true;
      }
      else {
        // 確保 response 是字符串，如果是對象則提取 text 或轉換為字符串
        // 帶 MCP Apps 互動畫面時 sendToGemini 回傳 { text, mcpApps }，text 可能為空字串（只有畫面）
        const responseMcpApps = typeof response === 'object' && response !== null ? response.mcpApps : null;
        const text = typeof response === 'string'
          ? response
          : (typeof response === 'object' && response.text
            ? response.text
            : (Array.isArray(responseMcpApps) && responseMcpApps.length > 0 ? '' : String(response)));
        // createdAt 使用回覆抵達當下的 finalCreatedAt（非送出當下固定的 botCreatedAt），
        // 讓顯示時間反映 AI 實際完成回覆的時間，而不是使用者發送訊息的時間
        // id 使用預生成的 assistantMessageId：與後端暫存列一致，補收/sync 依 id 收斂不重複
        const finalMessages = [...baseMessages, withMcpApps({ id: assistantMessageId, sender: 'chatbot', text, createdAt: finalCreatedAt }, responseMcpApps)];
        // 最終訊息一律交給 reveal 計時器落地（揭示動畫還沒追上就等追上；已追上則下一 tick 立即 commit）。
        // 不能在這裡直接呼叫 updateChatMessages 後讓 finally 立刻清掉該對話串流：
        // updateChatMessages 是 async（先 await IndexedDB 才更新狀態），若泡泡先被清掉、正式訊息晚一個 render 才落地，
        // 下方「新訊息加入」的捲動 effect 會把 messageCount 增加誤判成新訊息，
        // 執行 lastBotMessageRef.scrollIntoView({block:'start'}) 把畫面往上拉到回覆頂端（看起來像跳回圖片位置）。
        // reveal 計時器的 commit 流程保證「正式訊息落地完成 → 才清該對話串流」的順序，不會出現這個空檔。
        // 把該對話串流目標設為最終文字，讓逐字動畫（若此對話為當前檢視）追上它後再落地。
        setStreamFor(targetChatId, (prev) => ({ text, createdAt: prev?.createdAt || botCreatedAt }));
        pendingFinalByChatRef.current.set(targetChatId, finalMessages);
        deferredFinalize = true;
      }
    } catch (error) {
      console.error('外部 API 錯誤:', error);
      // 拋出型錯誤屬終局失敗（sendToGemini 已把網路類錯誤轉為 _sendFailed 物件回傳，不會走到這裡），
      // 標記該提問為已放棄，避免之後的觸發點（回前景/重連）誤啟補收。
      // 直接用 userMessage.id（即這次送出的提問）而非以 chatId 反查：遲到的送出反查到的可能已是下一題的 id，
      // 會誤把還在等回覆的新那一題標記成放棄
      markAbandoned(userMessage.id);
      // 若此對話的停止指令已成功執行，此處錯誤是因 Agent 被中斷，不需再顯示失敗訊息。
      // 已不是進行中的那一次（補收已落地、使用者已送出下一題）也不落地錯誤訊息，避免覆蓋新內容
      if (!stopSucceededByChatRef.current.has(targetChatId) && isStillOwner()) {
        const errorMessage = {
          sender: 'chatbot',
          text: `發送訊息失敗：${error.message}`
        };
        // 同樣交給 reveal 計時器以正確順序落地（先落地、後清泡泡）。
        // 若此對話正是當前檢視，把串流目標截斷到目前已揭示長度讓計時器視為「已追上」，下一 tick 即 commit；
        // 背景對話則由計時器即時落地，不需截斷。
        setStreamFor(targetChatId, (prev) => {
          if (!prev) return prev;
          const cut = targetChatId === currentChatIdRef.current ? prev.text.slice(0, revealedLenRef.current) : prev.text;
          return { text: cut, createdAt: prev.createdAt };
        });
        pendingFinalByChatRef.current.set(targetChatId, [...resolvedMessages, errorMessage]);
        deferredFinalize = true;
      }
    } finally {
      // 清除此對話的送出相關暫時狀態。
      // 全部包在 isStillOwner 內：這些狀態都以 chatId 為 key，若這次送出已經遲到（補收先落地、
      // 輸入框已解鎖、使用者送出了下一題），它們已屬於新那一題，遲到者清掉就會讓新那一題失去等待狀態
      // （輸入框提前解鎖、串流泡泡被清、甚至誤關 WebSocket 讓新那一題收不到 delta）。
      if (isStillOwner()) {
        // 這一題是被使用者按停止中止的：此時 GenAIus 可能還沒真的停下來（/stop 是非同步的），
        // 輸入框的解鎖、串流泡泡與連線的清除都交給 stopGeneration 在「後端確認停止」後統一處理，
        // 這裡若提前解鎖，使用者會在 GenAIus 還沒停時就送出下一題，並被稍後才到的停止收尾清掉新題狀態。
        const stoppedByUser = stopInProgressByChatRef.current.has(targetChatId)
          && stoppedUserMsgIdsRef.current.has(userMessage.id);
        stopSucceededByChatRef.current.delete(targetChatId);
        stoppedUserMsgIdsRef.current.delete(userMessage.id);
        sendAbortByChatRef.current.delete(targetChatId);
        // 保險：拋出型錯誤會略過上方的解除點，這裡確保此對話「送出進行中」旗標一定被清除
        sendInFlightByChatRef.current.delete(targetChatId);
        inFlightUserMsgIdByChatRef.current.delete(targetChatId);
        if (!stoppedByUser) {
          if (!deferredFinalize) {
            // 未延遲落地（如真人客服 WS 送出、上傳全失敗、已停止、答案已由補收落地等 early return）：
            // 立即清此對話等待狀態。（補收落地的情境其實已在補收端清過，這裡重複執行無害）
            removeLoading(targetChatId);
            setStreamFor(targetChatId, null);
            awaitingOwnFinalizeByChatRef.current.delete(targetChatId);
          }
          // 若延遲落地，該對話的 loading/串流/awaitingOwnFinalize 改由上方 reveal 計時器在動畫追上後負責清除
          // AI 回覆已結束（成功/失敗皆同），關閉本次為了等待串流才開的連線
          disableAi?.();
        }
      }
    }
  };

  const stopGeneration = async () => {
    const chatId = chatHistory.currentChatId;
    if (!chatId) return;
    // 停止收尾進行中（等後端確認 GenAIus 真的停了）就不再重複送出停止請求，避免使用者連點
    if (stopInProgressByChatRef.current.has(chatId)) return;
    // 必須在 abort 之前標記：被中止的那次 sendMessage 收尾要據此把解鎖交給本函式，而非自己提前解鎖
    stopInProgressByChatRef.current.add(chatId);
    setStoppingChatIds((prev) => (prev.has(chatId) ? prev : new Set(prev).add(chatId)));
    // 停止只作用於「目前檢視的對話」，且只作用於「該對話進行中的那一題」：
    // 以 userMessageId 標記，之後送出的下一題不受影響（見 stoppedUserMsgIdsRef 宣告處說明）。
    // 立即標記：防止該題 sendToGemini 回傳後覆蓋停止系統訊息，並立即取消它自己的 HTTP 請求
    const stoppedUserMsgId = inFlightUserMsgIdByChatRef.current.get(chatId);
    if (stoppedUserMsgId) stoppedUserMsgIdsRef.current.add(stoppedUserMsgId);
    sendAbortByChatRef.current.get(chatId)?.abort();
    // 這一題若已有補收輪詢在跑（fast-accept 模式下 /send 一受理就會起一輪），請它立刻收工：
    // 停止之後永遠等不到回覆，讓它睡到終局時限只會卡住同一對話下一題的補收（見該旗標宣告處說明）。
    if (stoppedUserMsgId) {
      recoveryStopUserMsgIdsRef.current.add(stoppedUserMsgId);
      wakeRecovery(chatId);
    }
    // 用戶主動停止：同步標記進行中提問為已放棄，之後的觸發點（回前景/重連）不再補收這則回覆。
    // 此標記在任何 await 之前執行，蓋掉「按停止後、下方系統通知尚未落地 IndexedDB 就被 OS 銷毀 WebView」的窄競態
    markAbandoned(stoppedUserMsgId);
    stopSucceededByChatRef.current.delete(chatId);
    // 串流中途按下停止：先保留目前已顯示的部分文字，停止後作為該則回覆的最終內容提交，避免遺失
    const stoppedStream = streamsRef.current.get(chatId);
    const partialStreamingText = stoppedStream && stoppedStream.text ? stoppedStream.text : null;
    try {
      await apiService.stopChat(chatId);
      stopSucceededByChatRef.current.add(chatId);
    } catch (err) {
      console.warn('Stop request failed:', err);
    } finally {
      // 收尾開始：此時 /stop 已回應（後端已等到 GenAIus 確認停止或逾時），可以結束「停止中」狀態。
      stopInProgressByChatRef.current.delete(chatId);
      setStoppingChatIds((prev) => {
        if (!prev.has(chatId)) return prev;
        const next = new Set(prev);
        next.delete(chatId);
        return next;
      });
      // 保險：若此對話在收尾前已被使用者送出更新的一題（理論上輸入框仍鎖著不會發生），
      // 不可用舊快照覆蓋訊息、也不可清掉新題的等待狀態，直接放手讓新題自己收尾
      const newerTurnId = inFlightUserMsgIdByChatRef.current.get(chatId);
      if (newerTurnId && newerTurnId !== stoppedUserMsgId) return;
      // 先擋住 reveal 計時器，避免它在下面 await 期間把該對話 pendingFinal 落地
      pendingFinalByChatRef.current.delete(chatId);
      awaitingOwnFinalizeByChatRef.current.delete(chatId);
      removePendingClear(chatId);
      // 使用者主動停止，等待期間開的連線也一併關閉
      disableAi?.();
      // 在對話中插入系統通知，告知使用者停止結果。
      // 與 sendMessage 收尾同理：必須等訊息真正落地（updateChatMessages 內部先 await IndexedDB）後
      // 才清掉該對話串流，否則泡泡先消失、訊息晚一個 render 落地，
      // messageCount 增加會被誤判成新訊息而觸發 block:'start' 的往上捲動
      const currentMsgs = getCurrentMessages();
      const messagesToCommit = partialStreamingText
        ? [...currentMsgs, { id: generateMessageId(), sender: 'chatbot', text: partialStreamingText }]
        : [...currentMsgs];
      const systemMsg = {
        id: generateMessageId(),
        sender: 'notice',
        text: stopSucceededByChatRef.current.has(chatId)
          ? '⏹ AI 回覆已停止'
          : '⚠️ 停止指令傳送失敗，AI 回覆仍在進行中',
      };
      messagesToCommit.push(systemMsg);
      await Promise.resolve(updateChatMessages(chatId, messagesToCommit));
      // 無論 Agent 是否成功停止，都解鎖此對話的前端 UI
      setStreamFor(chatId, null);
      removeLoading(chatId);
    }
  };

  /**
   * 處理來自 useCustomerServiceSubscription 的 AI 串流事件（AI_STREAM_DELTA / AI_STREAM_DONE / AI_STREAM_CLEAR）。
   * 只更新記憶體內對應對話的串流緩衝（streams），不呼叫 updateChatMessages，避免觸發 IndexedDB 讀寫。
   * event.sessionId 即該串流所屬的 chatId，據此更新對應對話（支援多對話同時串流）。
   * @param {{type: string, sessionId: string, text?: string}} event WebSocket 收到的 AI 串流事件
   */
  const handleAiStreamEvent = useCallback((event) => {
    if (!event || !event.sessionId) return;
    const sessionId = event.sessionId;
    if (event.type === 'AI_STREAM_DELTA') {
      if (pendingClearByChatRef.current.has(sessionId)) {
        // 前面收到過 CLEAR、延後套用：這段 delta 是「清空後的第一段新文字」，現在才真正丟棄舊內容。
        // revealedText/revealedLenRef 只服務「目前檢視的對話」，只有 sessionId 就是當前對話時才需要
        // 同步重置逐字揭示進度，避免動到背景對話時誤把畫面上其他對話的揭示進度歸零
        removePendingClear(sessionId);
        if (sessionId === currentChatIdRef.current) {
          revealedLenRef.current = 0;
          setRevealedText('');
        }
        setStreamFor(sessionId, { text: event.text || '', createdAt: new Date().toISOString() });
      } else {
        setStreamFor(sessionId, (prev) => (
          prev
            // 保留 prev.createdAt，避免每次 delta 都換一個新時間戳記
            ? { text: prev.text + (event.text || ''), createdAt: prev.createdAt }
            : { text: event.text || '', createdAt: new Date().toISOString() }
        ));
      }
    } else if (event.type === 'AI_STREAM_CLEAR') {
      // 後端遇到工具呼叫，判定前一輪已顯示的內容是過程文字，之後要整段捨棄重來。
      // 不在此刻立即清空畫面（那樣會在工具執行期間出現空白閃爍），只先記住，
      // 等下一段 AI_STREAM_DELTA 真的送達時才真正套用清空（見上方 handler）
      addPendingClear(sessionId);
    } else if (event.type === 'AI_STREAM_DONE') {
      // 不論走哪個分支，這個對話的串流都已經（或即將）結束，殘留的延後清空標記不再有意義
      removePendingClear(sessionId);
      // 該對話若正在等待「自己發送的訊息」收尾（awaitingOwnFinalizeByChat），收尾時機一律交給
      // sendMessage/揭示動畫的追趕邏輯決定，不要在這裡提前清空其串流——
      // 否則會把還在播放中的動畫目標值一起清掉（見上方 awaitingOwnFinalizeByChatRef 宣告處說明）。
      // 只有非本分頁自己觸發的情境（例如跨分頁/跨實例 stopChat 通知）才在這裡清空該對話串流。
      // change: unify-reply-completion-signal Section 4——快速受理模式下，本分頁自己送出的訊息
      // 也可能正睡在 sendMessage 的 _accepted 分支觸發的補收輪詢中，此時仍要嘗試喚醒，讓生成完成
      // 的瞬間就查詢，不必等下一輪 backoff；對「沒有輪詢在睡」的情況 wakeRecovery 是安全的 no-op
      // （回傳 false），故無條件呼叫不影響阻塞模式（flag 關閉）下的既有行為。
      const wokeOwnAcceptedRecovery = wakeRecovery(sessionId);
      if (awaitingOwnFinalizeByChatRef.current.has(sessionId)) return;
      setStreamFor(sessionId, null);
      // change: unify-reply-completion-signal——非本分頁觸發的完成事件不再是純粹的 no-op：
      // 嘗試喚醒該對話正睡在退避等待中的補收輪詢，或在確實有缺口時直接觸發一次查詢，
      // 讓其他分頁/裝置（收到同一 userId 層級的 WS 推播、但不是自己送出這則訊息）能更快得知
      // 答案已完成，不必等到自己的心跳輪詢週期（最長可達數十秒）。刻意不在此處落地內容或
      // 解鎖——「查完了沒」與「查到後怎麼辦」仍統一交給 recoverPendingReply 判斷（單一收尾入口，
      // 見其宣告處與 landReplyMessages），此處只負責「觸發去查」。
      if (wokeOwnAcceptedRecovery) return;
      if (findRecoverableReply(messagesForChatRef.current?.(sessionId) ?? [], sessionId)) {
        recoverPendingReplyRef.current?.('ws-done', null, sessionId);
      }
    }
  }, [setStreamFor, addPendingClear, removePendingClear]);

  // 目前正在檢視的對話是否處於「伺服器已確認 processing」的補收等待中：只看「這個對話」是否在
  // 集合裡，跟其他對話是否也在補收中無關——多個對話可以同時各自補收，切到哪個就看那個對話自己的狀態。
  const isRecovering = recoveringChatIds.has(chatHistory.currentChatId);
  // 只要目前對話還有未解決的提問（含冷卻中、尚未查或查無結果但還不確定放棄），就持續顯示
  // 「處理中」泡泡，同時也鎖住輸入（見 ChatRichTextarea 的 isAwaitingReply prop）——
  // 2026-08-05 實測發現「泡泡在轉、卻能送出第二題」比純粹不鎖更令人困惑，且有機會撞上
  // GenAIus 同一 session 一次僅處理一題、併發第二題只拿到空回覆的既有限制，改為兩者一致：
  // 只要還在等這題的答案（不論是否已確認 processing），這個對話就不能再送出下一題。
  // 例外：該對話的最終落地寫入失敗過（見 landingFailedChatIds 宣告處）——此時缺口永遠不會被填補，
  // 再鎖下去就是永久鎖死，故只對這種對話忽略 gap。isRecovering 那條刻意不加例外：補收若正在輪詢
  // 就該繼續鎖，維持「同一 GenAIus session 不併發第二題」的保護。
  const isAwaitingReply = isRecovering
    || (hasPendingGap(getCurrentMessages(), chatHistory.currentChatId)
        && !landingFailedChatIds.has(chatHistory.currentChatId));

  /**
   * 重新發送一則被標記為終局送達失敗的提問（change: unify-reply-completion-signal Section 5 後續）。
   *
   * <p>直接送出新提問前，先用原本的 userMessageId 補查一次 `/reply`：終局時限判定失敗時已經
   * `markAbandoned`（永久排除自動補收，見 failNotFound），但答案仍可能在那之後才真正寫進暫存表
   * ——若不補查就直接送出新提問，會讓 GenAIus 重新生成一次（浪費資源，且 LLM 非決定性，
   * 新答案不保證跟原本那個一樣），而原本已經算好的答案就永遠沒人看得到、白白留到 7 天 TTL 到期。
   *
   * <p>刻意不做成「背景自動翻盤」（例如終局時限判死後，之後查到答案就自動把失敗提示換成真正答案）——
   * 使用者已經明確看過一次「送達失敗」的結論，之後又無聲變回成功反而更困惑，訊號不一致。
   * 這裡是使用者主動按下重送這個明確動作觸發的即時檢查，找到答案就直接顯示，是可解釋的結果，
   * 不是背景偷偷改變已經展示過的結論。
   *
   * @param {{id: string, text: string}} message 被標記失敗的原始提問訊息
   */
  const resendMessage = useCallback(async (message) => {
    const chatId = chatHistory.currentChatId;
    if (chatId && message?.id) {
      try {
        const data = await apiService.getBufferedReply(chatId, message.id);
        if (data?.status === 'completed') {
          console.log('[重送] 補查發現答案其實已存在，直接落地，不重複發送', {
            chatId,
            userMessageId: message.id,
          });
          // 同補收 completed 分支：捲動意圖在落地前量測，落地成功才帶動畫面（AGUI-1082）
          const followToLatest = recoveryScrollRef.current.shouldFollow(chatId);
          const landResult = await landReplyMessages(chatId, [withMcpApps({
            id: data.assistantMessageId,
            sender: 'chatbot',
            text: data.replyText ?? '',
            createdAt: data.createdTime || new Date().toISOString(),
          }, data.mcpApps)], { append: true });
          if (landResult.ok) {
            if (followToLatest) recoveryScrollRef.current.scrollToLatest();
            clearDeliveryFailed(message.id);
            removeDeliveryFailedMsg(message.id);
            return;
          }
          // 落地寫入失敗：landReplyMessages 內部已完成失敗收尾，這裡不重複處理，
          // 落回下方直接重送，至少讓使用者拿得到一個答案
        }
      } catch (e) {
        console.warn('[重送] 補查失敗，改為直接重新發送', e);
      }
    }
    sendMessage(message?.text);
  }, [chatHistory.currentChatId, landReplyMessages, sendMessage, removeDeliveryFailedMsg]);

  return {
    messages,
    input,
    setInput,
    isLoading,
    isStopping,
    isRecovering,
    isAwaitingReply,
    sendMessage,
    resendMessage,
    stopGeneration,
    messagesEndRef,
    messagesContainerRef: setMessagesContainerRef,
    lastBotMessageRef,
    lastUserMessageRef,
    bottomSpacerRef,
    isFinalizeTransient,
    isPositioning,
    scrollToBottom,
    onAiStreamEvent: handleAiStreamEvent,
  };
};
